import { useMemo, useState, useEffect, useRef, useLayoutEffect, useCallback } from "react";
import {
  Box,
  Typography,
  IconButton,
  Tooltip,
  Skeleton,
  CircularProgress,
  useMediaQuery,
  alpha,
} from "@mui/material";
import { useTheme as useMuiTheme } from "@mui/material/styles";
import { ImageLightbox } from "../../components/ImageLightbox";
import { ArrowLeft, Calendar, Check, X, Sparkles, FileText, Clock, History, ChevronDown, ChevronUp, Send, RotateCcw, FileX, Save } from "lucide-react";

import { Badge } from "../../components/Badge";
import { Button } from "../../components/Button";
import { CustomTextField } from "../../components/CustomTextField";
import { CustomTextArea } from "../../components/CustomTextArea";
import { CustomSelector } from "../../components/CustomSelector";
import { MarkdownEditor } from "../../components/MarkdownEditor";
import { Label } from "../../components/Label";
import { CollapsibleContent } from "../../components/Collapsible";
import { useTheme as useCustomTheme } from "../../theme/useTheme";
import { platformColors, changeTypeColors } from "../../theme/theme";
import { useAuth } from "../../components/contexts/AuthContext";
import apiClient from "../../api-client";
import { getData } from "../../components/hooks/getData";
import { useClientReadOnly } from "../../hooks/useClientReadOnly";
import SuccessPopup from "../../components/SuccessPopup";
import { EmptyStateMessage } from "../../components/EmptyStateMessage";
import { UnifiedModal } from "../../components/UnifiedModal";
import { AgencyPromptsButton } from "../../components/prompts/AgencyPromptsButton";
import { ChatInterface } from "../Incidents/ChatInterface";
import { ImageCarousel } from "../../components/ImageCarousel";
import { ContentConfirmationModal } from "./Modals/ContentConfirmationModal";
import DiffMatchPatch from "diff-match-patch";
import { WorkflowStepper } from "./workflow/WorkflowStepper";
import { ReviewView } from "./workflow/ReviewView";
import UnsavedChangesBar from "../../components/UnsavedChangesBar";
import { SchedulingView } from "./workflow/SchedulingView";
import { BaseTextPanel } from "./workflow/BaseTextPanel";
import {
  getPieceDisplayStatus,
  getPieceWorkflow,
} from "./workflow/workflowUtils";
import { ADAPTATION_PLATFORMS, formatLabel, getPlatformMeta, isDocumentMedia, mediaErrorFrom } from "./workflow/platformMeta";
import { callToActionOf } from "./workflow/callToAction";
import {
  createConnectLink,
  cropAdaptationImage,
  documentUploadErrorText,
  publishingErrorText,
  syncClientSocial,
  updateAdaptationCallToAction,
  updateAdaptationFormat,
  updateDocumentTitle,
  uploadAdaptationDocument,
  uploadAdaptationVideo,
} from "../../services/socialPublishingService.js";
import { ConnectWarning } from "../Clients/social/ConnectWarning";

// Función para convertir markdown a HTML (síncrona, para el diff)
const markdownToHtmlSimple = (text) => {
  if (!text) return "";
  const normalizedText = text.replace(/\r\n/g, "\n");
  // Escapar HTML primero
  let html = normalizedText
    .replace(/&/g, "&amp;")
    .replace(/</g, "&lt;")
    .replace(/>/g, "&gt;");

  // Convertir markdown a HTML
  return html
    // Negrita ** o __
    .replace(/\*\*([^*]+)\*\*/g, "<strong>$1</strong>")
    .replace(/__([^_]+)__/g, "<strong>$1</strong>")
    // Cursiva * o _ (cuidado de no capturar los de negrita)
    .replace(/(?<!\*)\*([^*]+)\*(?!\*)/g, "<em>$1</em>")
    .replace(/(?<!_)_([^_]+)_(?!_)/g, "<em>$1</em>")
    // Enlaces [texto](url)
    .replace(/\[([^\]]+)\]\(([^)]+)\)/g, '<a href="$2" target="_blank" rel="noopener">$1</a>')
    // Saltos de línea: doble salto = párrafo, salto simple = espacio (markdown por defecto)
    .replace(/\n{2,}/g, "<br/><br/>")
    .replace(/\n/g, " ");
};

const normalizeMarkdownForCompare = (text) => {
  if (!text) return "";
  return text
    .replace(/\r\n/g, "\n")
    .replace(/\u00a0/g, " ")
    .replace(/[ \t]+$/gm, "")
    .trimEnd();
};

const CHANGE_TYPE_LABELS = {
  initial: "Generación inicial",
  manual_edit: "Edición manual",
  ai_iteration: "Iteración IA",
  revert: "Reversión",
};



// Language options for creation mode
const languageOptions = [
  { value: "es", label: "Español" },
  { value: "en", label: "English" },
  { value: "ca", label: "Català" },
  { value: "fr", label: "Français" },
  { value: "pt", label: "Português" },
];

// Communication style options
const toneOptions = [
  { value: "formal", label: "Formal", description: "Profesional y serio" },
  { value: "friendly", label: "Amigable", description: "Cercano y accesible" },
  { value: "technical", label: "Técnico", description: "Especializado y detallado" },
  { value: "casual", label: "Casual", description: "Relajado e informal" },
  { value: "professional", label: "Profesional", description: "Competente y confiable" },
  { value: "creative", label: "Creativo", description: "Innovador y original" },
];



const languageTypeOptions = [
  { value: "emotional", label: "Emocional", description: "Apela a sentimientos" },
  { value: "rational", label: "Racional", description: "Basado en hechos y lógica" },
  { value: "direct", label: "Directo", description: "Claro y al grano" },
  { value: "inspirational", label: "Inspiracional", description: "Motivador y aspiracional" },
  { value: "narrative", label: "Narrativo", description: "Cuenta historias" },
];


// Polling configuration
const ITERATION_PROMPT_STEPS = ["iteration"];

const POLLING_INTERVAL_MS = 1000;
const MAX_POLLING_ATTEMPTS = 60;

const formatDateTimeInputValue = (dateString) => {
  if (!dateString) return "";
  const date = new Date(dateString);
  if (Number.isNaN(date.getTime())) return "";
  const pad = (value) => String(value).padStart(2, "0");
  return `${date.getFullYear()}-${pad(date.getMonth() + 1)}-${pad(date.getDate())}T${pad(
    date.getHours()
  )}:${pad(date.getMinutes())}`;
};

const formatScheduleLabel = (dateString) => {
  if (!dateString) return "Programar fecha";
  const date = new Date(dateString);
  if (Number.isNaN(date.getTime())) return "Programar fecha";
  return date.toLocaleDateString("es-ES", {
    day: "2-digit",
    month: "short",
    year: "numeric",
    hour: "2-digit",
    minute: "2-digit",
  });
};

export function ContentPieceView({
  onClose,
  contentDetail,
  isLoading,
  initialAdaptationId = null,
  initialWorkflowView = null, // step to open first instead of the piece's current one
  onSaveChanges,
  onIterateAdaptation,
  iteratingAdaptationId,
  versionsRefreshKey: propVersionsRefreshKey = 0,
  // Creation mode props
  mode = "edit", // "edit" | "create"
  configId = null,
  initialLanguage = "es",
  onGenerationComplete = null, // Callback after generation is complete
  externalHeader = false, // the page shows the title (and status) in its own header
  onStatusChange = null, // (displayStatus) => void — to keep that header badge in sync
  onPieceRefreshed = null, // (piece) => void — piece re-fetched after a workflow action (e.g. scheduling)
  // Client view props
  isClientView = false, // If true, shows simplified client view
  // Direct piece metadata from list endpoint (for immediate modal type detection without waiting for fetch)
  hasRawContent = null, // true/false from list endpoint, null if not provided
  hasPlatformAdaptations = null, // true/false from list endpoint, null if not provided
  pieceTitle = "", // Title from list endpoint for no_content step
  pieceErrorMessage = null, // Error message from list endpoint if generation failed
  // Tutorial / guided mode
  isTutorialActive = false,
  onCreationStepChange = null, // (step: string) => void — notifies parent of step changes
  tutorialSelectedPlatforms = null, // tutorial: networks shown already ticked on the base text
  tutorialWorkflow = null, // tutorial: { messages, versions, social } mocks
}) {
  const muiTheme = useMuiTheme();
  const isMobile = useMediaQuery(muiTheme.breakpoints.down("md"));
  const { neutrals, colors, theme } = useCustomTheme();
  const { interceptorReady, user } = useAuth();
  const isFreePlanReadOnly = useClientReadOnly();
  const [drafts, setDrafts] = useState({});
  const [isSaving, setIsSaving] = useState(false);
  const [previewImageUrl, setPreviewImageUrl] = useState("");
  const [showIterateDialog, setShowIterateDialog] = useState(false);
  const [iteratePrompt, setIteratePrompt] = useState("");

  // Raw content iteration states
  const [isIteratingRawContent, setIsIteratingRawContent] = useState(false);
  const [showIterateRawDialog, setShowIterateRawDialog] = useState(false);
  const [iterateRawPrompt, setIterateRawPrompt] = useState("");

  // Local state for contentDetail to allow updates during adaptation generation
  const [localContentDetail, setLocalContentDetail] = useState(null);


  // Creation mode states
  // Steps: "input" | "no_content" | "generating_raw" | "raw_ready" | "generating_adaptations" | "complete"
  const [creationStep, setCreationStepRaw] = useState(() => {
    if (mode === "create") return "input";
    if (isClientView) return "complete";
    // Manual pieces have adaptations but no base text.
    if (hasPlatformAdaptations === true) return "complete";
    if (hasRawContent === true) return "raw_ready";
    return "no_content";
  });
  const setCreationStep = useCallback((step) => {
    setCreationStepRaw(step);
    if (onCreationStepChange) onCreationStepChange(step);
  }, [onCreationStepChange]);
  const [topic, setTopic] = useState("");
  const [selectedLanguage, setSelectedLanguage] = useState(initialLanguage);
  const [selectedPlatforms, setSelectedPlatforms] = useState([]);
  const [selectedTone, setSelectedTone] = useState("formal");
  const [selectedLanguageType, setSelectedLanguageType] = useState("direct");
  const [rawContent, setRawContent] = useState("");
  const [rawContentBaseline, setRawContentBaseline] = useState("");
  const [generatedPieceId, setGeneratedPieceId] = useState(null);
  const [generatedAdaptations, setGeneratedAdaptations] = useState([]);
  const [localSelectedAdaptationId, setLocalSelectedAdaptationId] = useState(initialAdaptationId);
  const [, setIsGenerating] = useState(false);
  const [showSendToClientDialog, setShowSendToClientDialog] = useState(false);
  const [showApproveDialog, setShowApproveDialog] = useState(false);

  // Campaign configuration states

  // Version history states
  const [expandedVersionId, setExpandedVersionId] = useState(null);
  const [versionsRefreshKey, setVersionsRefreshKey] = useState(0);
  const [showVersionsSkeleton, setShowVersionsSkeleton] = useState(false);
  const [isReverting, setIsReverting] = useState(false);
  const [showVersionHistoryDialog, setShowVersionHistoryDialog] = useState(false);
  const [conversationRefreshKey, setConversationRefreshKey] = useState(0);
  const [conversationMessages, setConversationMessages] = useState([]);
  const [hasConversationHydrated, setHasConversationHydrated] = useState(false);

  // Popup states
  const [popupOpen, setPopupOpen] = useState(false);
  const [popupMessage, setPopupMessage] = useState("");
  const [popupIsError, setPopupIsError] = useState(false);
  const [popupDuration, setPopupDuration] = useState(3000);

  // Polling refs
  const pollingTimeoutRef = useRef(null);
  const isMountedRef = useRef(false);
  const hasInitializedCreateDraftsRef = useRef(false);

  // Helper function to show popup
  const showPopup = (message, isError = false, duration = null) => {
    setPopupMessage(message);
    setPopupIsError(isError);
    setPopupDuration(duration !== null ? duration : isError ? 3000 : 1500);
    setPopupOpen(true);
  };

  // Sync contentDetail prop with local state
  useEffect(() => {
    if (contentDetail) {
      setLocalContentDetail(contentDetail);
    }
  }, [contentDetail]);

  const initializedContentIdRef = useRef(null);

  // Determine the initial step on mount from the piece flags (create starts at the input form).
  useEffect(() => {
    setConversationRefreshKey((prev) => prev + 1);

    if (mode === "create") {
      setCreationStep("input");
      setSelectedLanguage(initialLanguage);
    } else if (mode === "edit") {
      // Use props from list endpoint to immediately determine step (without waiting for contentDetail)
      const hasRaw = hasRawContent === true;
      const hasAdapt = hasPlatformAdaptations === true;

      if (isClientView) {
        // For client view, always go to "complete" step since clients only see content
        // that already has adaptations (status client_review or client_approved)
        setCreationStep("complete");
        return;
      }

      if (hasAdapt) {
        // Case 2: Has platform adaptations (manual pieces have no raw content) - show adaptations editor
        setCreationStep("complete");
      } else if (hasRaw && !hasAdapt) {
        // Case 1: Has raw content but no adaptations - show raw editor with generate button
        setCreationStep("raw_ready");
        setTopic(pieceTitle || "");
      } else if (!hasRaw && !hasAdapt) {
        // Case 3: No raw content and no adaptations - show generate raw content step
        setCreationStep("no_content");
        setTopic(pieceTitle || "");
      }
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  // Fill in data from contentDetail when it loads (but don't change creationStep if already set)
  useEffect(() => {
    if (mode !== "edit" || !localContentDetail) return;

    // Only initialize data once per content piece
    if (initializedContentIdRef.current === localContentDetail.id) return;
    initializedContentIdRef.current = localContentDetail.id;

    // Fill in data based on already determined creationStep
    if (creationStep === "raw_ready") {
      setRawContent(localContentDetail.raw_content || "");
      setRawContentBaseline(localContentDetail.raw_content || "");
      setGeneratedPieceId(localContentDetail.id);
      // No network preselected (except in the tutorial): the consultant picks them on the base text.
      setSelectedPlatforms(tutorialSelectedPlatforms || []);
      setGeneratedAdaptations([]);
      setLocalSelectedAdaptationId(null);
      setIsGenerating(false);
    } else if (creationStep === "no_content") {
      // Robustness check: if list said no_content but detail reveals it has raw_content,
      // switch to raw_ready step
      const hasActualRawContent = localContentDetail.raw_content && localContentDetail.raw_content.trim() !== "";

      if (hasActualRawContent) {
        // Has raw content - should be raw_ready, not no_content
        setCreationStep("raw_ready");
        setRawContent(localContentDetail.raw_content || "");
        setRawContentBaseline(localContentDetail.raw_content || "");
        setGeneratedPieceId(localContentDetail.id);
        setSelectedPlatforms([]);
        setGeneratedAdaptations([]);
        setLocalSelectedAdaptationId(null);
        setIsGenerating(false);
      } else {
        // Truly no content - remain in no_content step
        setGeneratedPieceId(localContentDetail.id);
        setSelectedPlatforms([]);
      }
    } else if (creationStep === "complete" && !isClientView) {
      // Adaptations come from localContentDetail; the base text stays visible in step 1.
      setRawContent(localContentDetail.raw_content || "");
      setRawContentBaseline(localContentDetail.raw_content || "");
      setGeneratedPieceId(localContentDetail.id);
    }
  }, [mode, localContentDetail, creationStep, isClientView, tutorialSelectedPlatforms]);

  // Stop polling when the page unmounts
  useEffect(() => {
    isMountedRef.current = true;
    return () => {
      isMountedRef.current = false;
      if (pollingTimeoutRef.current) {
        clearTimeout(pollingTimeoutRef.current);
        pollingTimeoutRef.current = null;
      }
    };
  }, []);




  // Toggle platform selection
  const togglePlatform = (platformValue) => {
    setSelectedPlatforms(prev =>
      prev.includes(platformValue)
        ? prev.filter(p => p !== platformValue)
        : [...prev, platformValue]
    );
  };

  // Handle content generation
  const handleGenerateContent = async () => {
    if (!configId) {
      showPopup("No se ha seleccionado una configuración de cliente.", true);
      return;
    }
    if (!topic.trim()) {
      showPopup("Por favor, introduce un título para el contenido.", true);
      return;
    }

    setIsGenerating(true);
    setCreationStep("generating_raw");

    try {
      // Step 1: PATCH the config with language and communication style
      await apiClient.patch(`/content-generation/configs/${configId}/`, {
        language: selectedLanguage,
        tone: selectedTone,
        language_type: selectedLanguageType,
      });

      // Step 2: Create content piece and start generation
      const response = await apiClient.post("/content-generation/pieces/", {
        config: configId,
        title: topic.trim(),
      });

      const pieceId = response.data.id;
      const taskId = response.data.task_id;
      setGeneratedPieceId(pieceId);

      // Poll for task completion
      let attempts = 0;
      const maxAttempts = MAX_POLLING_ATTEMPTS;

      const pollTaskStatus = async () => {
        // Cancel polling if modal was closed or auth not ready
        if (!isMountedRef.current || !interceptorReady) {
          pollingTimeoutRef.current = null;
          return;
        }

        try {
          const statusResponse = await apiClient.get(`/content-generation/task-status/${taskId}/`);
          const { status, error } = statusResponse.data;

          if (status === 'SUCCESS') {
            pollingTimeoutRef.current = null;
            // Task completed, fetch raw content
            const pieceResponse = await apiClient.get(`/content-generation/pieces/${pieceId}/`);
            const piece = pieceResponse.data;

            if (piece.raw_content && piece.raw_content.trim() !== "") {
              setLocalContentDetail(piece);
              setRawContent(piece.raw_content);
              setRawContentBaseline(piece.raw_content || "");
              setSelectedPlatforms([]);
              setCreationStep("raw_ready");
              setIsGenerating(false);
            } else {
              // Raw content generated but empty - go back to input
              showPopup("No se generó contenido. Inténtalo de nuevo.", true);
              setCreationStep("input");
              setIsGenerating(false);
            }
            return;
          } else if (status === 'FAILURE') {
            pollingTimeoutRef.current = null;
            showPopup(error || "Error al generar el contenido.", true);
            setCreationStep("input");
            setIsGenerating(false);
            return;
          }

          // Still running, continue polling
          attempts++;
          if (attempts < maxAttempts && isMountedRef.current) {
            pollingTimeoutRef.current = setTimeout(pollTaskStatus, POLLING_INTERVAL_MS);
          } else {
            pollingTimeoutRef.current = null;
            showPopup("Timeout: La generación está tardando demasiado.", true);
            setCreationStep("input");
            setIsGenerating(false);
          }
        } catch (pollErr) {
          console.error("Error polling task status:", pollErr);
          attempts++;
          if (attempts < maxAttempts && isMountedRef.current) {
            pollingTimeoutRef.current = setTimeout(pollTaskStatus, POLLING_INTERVAL_MS);
          } else {
            pollingTimeoutRef.current = null;
            showPopup("Error al verificar el estado de la generación.", true);
            setCreationStep("input");
            setIsGenerating(false);
          }
        }
      };

      // Start polling
      pollingTimeoutRef.current = setTimeout(pollTaskStatus, 500);

    } catch (err) {
      console.error("Error creating content piece:", err);
      showPopup(err.response?.data?.detail || "Error al crear el contenido.", true);
      setCreationStep("input");
      setIsGenerating(false);
    }
  };

  // Handle confirming raw content and generating adaptations
  const handleConfirmRaw = async () => {
    if (!generatedPieceId) {
      showPopup("No hay contenido para procesar.", true);
      return;
    }
    if (selectedPlatforms.length === 0) {
      showPopup("Selecciona al menos una plataforma para generar adaptaciones.", true);
      return;
    }

    setIsGenerating(true);
    setCreationStep("generating_adaptations");

    try {
      const shouldPatchRawContent = rawContent !== rawContentBaseline;
      const patchPayload = {};
      if (shouldPatchRawContent) {
        patchPayload.raw_content = rawContent;
      }
      if (Object.keys(patchPayload).length > 0) {
        await apiClient.patch(`/content-generation/pieces/${generatedPieceId}/`, patchPayload);
        if (shouldPatchRawContent) {
          setRawContentBaseline(rawContent);
        }
      }

      // Start adaptation generation and get task_id
      const response = await apiClient.post(`/content-generation/pieces/${generatedPieceId}/generate_adaptations/`, {
        platforms: selectedPlatforms,
      });
      const taskId = response.data.task_id;

      // Poll for task completion
      let attempts = 0;
      const maxAttempts = MAX_POLLING_ATTEMPTS;

      const pollTaskStatus = async () => {
        // Cancel polling if modal was closed or auth not ready
        if (!isMountedRef.current || !interceptorReady) {
          pollingTimeoutRef.current = null;
          return;
        }

        try {
          const statusResponse = await apiClient.get(`/content-generation/task-status/${taskId}/`);
          const { status, error } = statusResponse.data;

          if (status === 'SUCCESS') {
            pollingTimeoutRef.current = null;
            // Task completed, fetch adaptations
            const pieceResponse = await apiClient.get(`/content-generation/pieces/${generatedPieceId}/`);
            const piece = pieceResponse.data;

            if (piece.platform_adaptations && piece.platform_adaptations.length > 0) {
              setGeneratedAdaptations(piece.platform_adaptations);
              setDrafts({}); // Reset drafts for new adaptations
              setCreationStep("complete");
              setIsGenerating(false);
              showPopup("Adaptaciones generadas correctamente");

              // Also update local contentDetail and call callback
              setLocalContentDetail(piece);
              if (onGenerationComplete) {
                onGenerationComplete(piece);
              }
            } else {
              // Adaptations generated but empty - go back to raw_ready
              showPopup("No se generaron adaptaciones. Inténtalo de nuevo.", true);
              setCreationStep("raw_ready");
              setIsGenerating(false);
            }
            return;
          } else if (status === 'FAILURE') {
            pollingTimeoutRef.current = null;
            showPopup(error || "Error al generar adaptaciones.", true);
            setCreationStep("raw_ready");
            setIsGenerating(false);
            return;
          }

          // Still running, continue polling
          attempts++;
          if (attempts < maxAttempts && isMountedRef.current) {
            pollingTimeoutRef.current = setTimeout(pollTaskStatus, POLLING_INTERVAL_MS);
          } else {
            pollingTimeoutRef.current = null;
            showPopup("Timeout: La generación de adaptaciones está tardando demasiado.", true);
            setCreationStep("raw_ready");
            setIsGenerating(false);
          }
        } catch (pollErr) {
          console.error("Error polling task status:", pollErr);
          attempts++;
          if (attempts < maxAttempts && isMountedRef.current) {
            pollingTimeoutRef.current = setTimeout(pollTaskStatus, POLLING_INTERVAL_MS);
          } else {
            pollingTimeoutRef.current = null;
            showPopup("Error al verificar el estado de la generación.", true);
            setCreationStep("raw_ready");
            setIsGenerating(false);
          }
        }
      };

      // Start polling
      pollingTimeoutRef.current = setTimeout(pollTaskStatus, 500);

    } catch (err) {
      console.error("Error generating adaptations:", err);
      showPopup(err.response?.data?.error || "Error al generar adaptaciones.", true);
      setCreationStep("raw_ready");
      setIsGenerating(false);
    }
  };

  // Unify adaptations: use generatedAdaptations if available (creation mode), otherwise use from contentDetail
  const detailAdaptations = generatedAdaptations.length > 0
    ? generatedAdaptations
    : (localContentDetail?.platform_adaptations || []);
  const contentThread = localContentDetail?.thread || null;
  const contentThreadId = contentThread?.id || null;
  const contentThreadParticipants = contentThread?.participants || [];
  const effectiveSelectedAdaptationId = localSelectedAdaptationId;

  const selectedAdaptation = useMemo(() => {
    if (!detailAdaptations.length) return null;
    return (
      detailAdaptations.find(
        (adaptation) => adaptation.id === effectiveSelectedAdaptationId
      ) || detailAdaptations[0]
    );
  }, [detailAdaptations, effectiveSelectedAdaptationId]);

  useEffect(() => {
    if (!detailAdaptations.length) return;
    const isValidSelection =
      localSelectedAdaptationId &&
      detailAdaptations.some((adaptation) => adaptation.id === localSelectedAdaptationId);
    if (!isValidSelection) {
      setLocalSelectedAdaptationId(detailAdaptations[0].id);
    }
  }, [detailAdaptations]); // Removed localSelectedAdaptationId to prevent infinite loop

  const normalizedStatus =
    localContentDetail?.status === "client_approved"
      ? "accepted"
      : localContentDetail?.status === "client_review"
        ? "review"
        : localContentDetail?.status || "draft";
  // Clients never edit content: they ask for changes on the piece thread.
  const isReadOnly = isClientView || isFreePlanReadOnly;
  const isDraft = normalizedStatus === "draft";
  const isReview = normalizedStatus === "review";
  // Hand-written networks, on manual or AI pieces, until the client approves.
  const canAddNetworks = !isReadOnly && (isDraft || isReview);
  const hasResolvedStatus = Boolean(localContentDetail?.status) || mode === "create";
  const canShowStatusAction =
    !isClientView && !isFreePlanReadOnly && hasResolvedStatus && (isDraft || isReview);
  const canShowClientAcceptAction =
    isClientView && hasResolvedStatus && normalizedStatus !== "accepted";

  // Group-aware vars
  const pieceGroupId = localContentDetail?.group_id ?? null;
  const pieceGroupName = localContentDetail?.group_name ?? null;
  const groupAllPiecesReady = localContentDetail?.group_all_pieces_ready ?? null;
  const actionPieceId = mode === "create" ? generatedPieceId : localContentDetail?.id;
  const statusActionLabel = isDraft ? "Enviar al cliente" : "Marcar como aceptado";
  const statusActionIcon = isDraft ? <Send size={16} /> : <Check size={16} />;
  const statusActionDisabled = !actionPieceId || isSaving;

  const draftForSelected = selectedAdaptation
    ? drafts[selectedAdaptation.id] || {}
    : {};
  const originalText = selectedAdaptation?.text || "";
  const originalScheduledAt = formatDateTimeInputValue(
    selectedAdaptation?.scheduled_at
  );

  // Multiple images support
  // originalImages: persisted images from the server
  // The LinkedIn PDF has no image: it is only shown in the review cards.
  const originalImages = (selectedAdaptation?.images || []).filter((img) => !isDocumentMedia(img)).map((img) => ({
    id: img.id,
    url: img.image,
    isPending: false,
  }));
  // pendingImages: new images not yet saved (held in draft)
  const pendingImages = draftForSelected.pendingImages || [];
  // imageIdsToRemove: IDs of server images queued for deletion
  const imageIdsToRemove = new Set(draftForSelected.imageIdsToRemove || []);
  // currentImages shown in the UI: keep originals minus removed, plus pending
  const currentImages = [
    ...originalImages.filter((img) => !imageIdsToRemove.has(img.id)),
    ...pendingImages,
  ];

  const draftText = draftForSelected.text ?? originalText;
  const isIterating =
    Boolean(selectedAdaptation?.id) &&
    selectedAdaptation.id === iteratingAdaptationId;
  const canIterate = typeof onIterateAdaptation === "function";
  const hasTextChanges =
    normalizeMarkdownForCompare(draftText) !==
    normalizeMarkdownForCompare(originalText);
  const hasImageChanges =
    pendingImages.length > 0 || imageIdsToRemove.size > 0;
  const hasContentChanges = hasTextChanges || hasImageChanges;
  const canShowSaveAction =
    Boolean(selectedAdaptation) &&
    !isReadOnly &&
    typeof onSaveChanges === "function";
  const canSaveContent = canShowSaveAction && hasContentChanges && !isSaving;
  const canShowHistoryAction = !isClientView && Boolean(selectedAdaptation);
  const discardContentChanges = () =>
    setDrafts((prev) => ({
      ...prev,
      [selectedAdaptation.id]: { ...prev[selectedAdaptation.id], text: originalText, pendingImages: [], imageIdsToRemove: [] },
    }));

  // Initialize drafts for edit mode (using detailAdaptations)
  useEffect(() => {
    if (!selectedAdaptation) return;
    setDrafts((prev) => {
      if (prev[selectedAdaptation.id]) return prev;
      return {
        ...prev,
        [selectedAdaptation.id]: {
          text: originalText,
          scheduled_at: originalScheduledAt,
        },
      };
    });
  }, [selectedAdaptation, originalText, originalScheduledAt]);

  // Initialize drafts for creation mode (using generatedAdaptations)
  useEffect(() => {
    if (!generatedAdaptations.length) return;
    setDrafts((prev) => {
      const newDrafts = { ...prev };
      let hasChanges = false;

      generatedAdaptations.forEach((adaptation) => {
        if (!newDrafts[adaptation.id]) {
          newDrafts[adaptation.id] = {
            text: adaptation.text || "",
            scheduled_at: formatDateTimeInputValue(adaptation.scheduled_at),
          };
          hasChanges = true;
        }
      });

      return hasChanges ? newDrafts : prev;
    });
  }, [generatedAdaptations]);

  // Ensure creation mode starts with backend values when reaching complete
  useEffect(() => {
    if (mode !== "create") return;
    if (creationStep !== "complete") {
      hasInitializedCreateDraftsRef.current = false;
      return;
    }
    if (hasInitializedCreateDraftsRef.current) return;
    if (!detailAdaptations.length) return;

    const nextDrafts = {};
    detailAdaptations.forEach((adaptation) => {
      nextDrafts[adaptation.id] = {
        text: adaptation.text || "",
        scheduled_at: formatDateTimeInputValue(adaptation.scheduled_at),
      };
    });
    setDrafts(nextDrafts);
    hasInitializedCreateDraftsRef.current = true;
  }, [mode, creationStep, detailAdaptations]);

  // Build versions endpoint - use adaptation ID, not piece ID
  const versionsAdaptationId = selectedAdaptation?.id;
  const hasValidAdaptation = Boolean(versionsAdaptationId);
  const versionsEndpoint = hasValidAdaptation
    ? `content-generation/adaptations/${versionsAdaptationId}/versions/`
    : null;

  // Only fetch versions when we have a valid adaptation ID and are in complete step AND the version history dialog is open
  const shouldFetchVersions =
    !isClientView && interceptorReady && hasValidAdaptation && creationStep === "complete" && showVersionHistoryDialog;

  // Combine refresh keys: local + prop (from parent when selecting content)
  const combinedRefreshKey = versionsRefreshKey + propVersionsRefreshKey;

  // Fetch versions using getData hook - only pass endpoint when valid to avoid null API calls
  const { data: versionsData, isLoading: isLoadingVersions } = getData(
    versionsEndpoint || "",  // Fallback to empty string to prevent null endpoint errors
    shouldFetchVersions,     // This will be false when versionsAdaptationId is null
    [combinedRefreshKey, versionsAdaptationId]
  );

  // Sync versions data
  const versions = versionsData || [];

  const shouldFetchConversation =
    interceptorReady && creationStep === "complete" && Boolean(contentThreadId);
  const {
    data: conversationMessagesData,
    isLoading: isLoadingConversation,
    isFetching: isFetchingConversation,
  } = getData(
    contentThreadId ? `/communication/threads/${contentThreadId}/messages/all` : null,
    shouldFetchConversation,
    [contentThreadId, conversationRefreshKey]
  );

  // Reset conversation UI before paint when entering the conversation step.
  // This avoids showing empty-state placeholders for one frame on modal open.
  useLayoutEffect(() => {
    if (creationStep !== "complete") return;
    setConversationMessages([]);
    setHasConversationHydrated(false);
  }, [creationStep, contentThreadId]);

  useEffect(() => {
    setConversationMessages([]);
    if (!contentThreadId) {
      setHasConversationHydrated(true);
      return;
    }
    setHasConversationHydrated(false);
  }, [contentThreadId]);

  useEffect(() => {
    if (!contentThreadId) return;
    if (!Array.isArray(conversationMessagesData)) return;

    setHasConversationHydrated(true);
    setConversationMessages((prev) => {
      const backendIds = new Set(conversationMessagesData.map((msg) => msg.id));
      const localPending = prev.filter(
        (msg) => !backendIds.has(msg.id) && String(msg.id || "").startsWith("temp-")
      );
      return [...conversationMessagesData, ...localPending];
    });
  }, [contentThreadId, conversationMessagesData]);

  useEffect(() => {
    if (!contentThreadId) return;
    if (!shouldFetchConversation) return;
    if (Array.isArray(conversationMessagesData)) return;
    if (!isLoadingConversation && !isFetchingConversation) {
      setHasConversationHydrated(true);
    }
  }, [
    contentThreadId,
    shouldFetchConversation,
    conversationMessagesData,
    isLoadingConversation,
    isFetchingConversation,
  ]);

  const shouldShowConversationSkeleton =
    creationStep === "complete" &&
    !hasConversationHydrated;

  // Delayed skeleton - only show if loading takes more than 300ms
  useEffect(() => {
    let timeout;
    if (isLoadingVersions) {
      timeout = setTimeout(() => setShowVersionsSkeleton(true), 300);
    } else {
      setShowVersionsSkeleton(false);
    }
    return () => clearTimeout(timeout);
  }, [isLoadingVersions]);

  // Trigger refetch by incrementing refresh key
  const refetchVersions = () => {
    setVersionsRefreshKey((prev) => prev + 1);
  };

  const handleConversationMessagesUpdate = (messageData, action = "add") => {
    if (action === "delete") {
      setConversationMessages((prev) =>
        prev.filter((msg) => msg.id !== messageData.id)
      );
      return;
    }

    if (action === "update") {
      setConversationMessages((prev) =>
        prev.map((msg) =>
          msg.id === messageData.id
            ? { ...msg, content: messageData.content, updated_at: messageData.updated_at }
            : msg
        )
      );
      return;
    }

    setConversationMessages((prev) => [...prev, messageData]);
  };

  const handleConversationRefresh = () => {
    setConversationRefreshKey((prev) => prev + 1);
  };

  // ── Workflow (Generación → Revisión → Programación) ──
  const [workflowView, setWorkflowView] = useState(null);
  // A video uploading from a card: changing step would unmount it and lose the upload.
  const [isUploadingMedia, setIsUploadingMedia] = useState(false);
  const goToStep = (key) => {
    if (isUploadingMedia && key !== workflowView) {
      showPopup("Espera a que termine de subirse el vídeo antes de cambiar de paso.", true);
      return;
    }
    setWorkflowView(key);
  };
  useEffect(() => {
    if (!isUploadingMedia) return undefined;
    const warn = (event) => {
      event.preventDefault();
      event.returnValue = "";
    };
    window.addEventListener("beforeunload", warn);
    return () => window.removeEventListener("beforeunload", warn);
  }, [isUploadingMedia]);
  const workflowInitializedIdRef = useRef(null);
  const workflow = useMemo(
    () =>
      getPieceWorkflow(
        localContentDetail
          ? { ...localContentDetail, platform_adaptations: detailAdaptations }
          : null,
        { isClient: isClientView }
      ),
    [localContentDetail, detailAdaptations, isClientView]
  );
  const isPastGeneration = workflow.current !== "generation";
  const isSchedulingLocked = workflow.steps.some((step) => step.key === "scheduling" && step.disabled);
  // The client always stays on the review step (no stepper): cards, comments and approval.
  const activeWorkflowView = isClientView
    ? "review"
    : isPastGeneration && workflowView === "generation"
      ? workflow.current
      : workflowView || workflow.current;
  // Pieces with only a base text already live in the workflow: step 1 shows that text.
  const isBaseTextStep =
    !isClientView && (creationStep === "raw_ready" || creationStep === "generating_adaptations");
  const showWorkflow = (creationStep === "complete" || isBaseTextStep) && Boolean(localContentDetail?.id);
  // With the title in the page header, the workflow is laid out as loose boxes (no frame).
  const isFrameless = externalHeader && showWorkflow;

  const displayStatusLabel = localContentDetail?.status ? getPieceDisplayStatus(localContentDetail).label : null;
  useEffect(() => {
    if (!onStatusChange || !localContentDetail?.status) return;
    onStatusChange({
      ...getPieceDisplayStatus(localContentDetail),
      hasErrors: Boolean(localContentDetail?.has_publication_errors),
    });
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [onStatusChange, displayStatusLabel, localContentDetail?.has_publication_errors]);

  // Open the step the piece is in, once per piece (after its thread has loaded).
  useEffect(() => {
    if (!showWorkflow || !hasConversationHydrated) return;
    if (workflowInitializedIdRef.current === localContentDetail.id) return;
    workflowInitializedIdRef.current = localContentDetail.id;
    const initialStep = workflow.steps.find((step) => step.key === initialWorkflowView && !step.disabled);
    setWorkflowView(initialStep ? initialStep.key : workflow.current);
  }, [showWorkflow, hasConversationHydrated, localContentDetail?.id, workflow, initialWorkflowView]);

  // Client config → client id, needed for the social connections of Programación.
  const { data: workflowConfigDetail } = getData(
    localContentDetail?.config ? `content-generation/configs/${localContentDetail.config}/` : "",
    interceptorReady && !isClientView && showWorkflow && Boolean(localContentDetail?.config),
    [localContentDetail?.config]
  );
  const workflowClientId = workflowConfigDetail?.client ?? null;

  // Subir un vídeo pide la red conectada: el portal se abre en otra pestaña para no perder el
  // archivo local, y al volver a esta se comprueba la conexión. { platform, resolve } mientras se avisa.
  const [connectRequest, setConnectRequest] = useState(null);
  const handleConnectNetwork = (platform) =>
    new Promise((resolve) => setConnectRequest({ platform, resolve }));

  const openConnectPortal = async ({ platform, resolve }) => {
    setConnectRequest(null);
    // Abierta en el clic: tras el await el navegador la bloquearía.
    const portalTab = window.open("", "_blank");
    if (!portalTab) {
      showPopup("El navegador ha bloqueado la pestaña de conexión. Permite las ventanas emergentes.", true);
      resolve(false);
      return;
    }
    try {
      portalTab.location.href = await createConnectLink(workflowClientId, window.location.pathname);
    } catch (err) {
      portalTab.close();
      showPopup(publishingErrorText(err, "No se ha podido abrir la conexión."), true);
      resolve(false);
      return;
    }
    const onReturn = async () => {
      window.removeEventListener("focus", onReturn);
      try {
        const social = await syncClientSocial(workflowClientId);
        const connected = (social?.accounts || []).some(
          (account) => account.platform === platform && account.status === "connected"
        );
        if (!connected) showPopup(`${getPlatformMeta(platform).label} sigue sin estar conectada.`, true);
        resolve(connected);
      } catch (err) {
        showPopup(publishingErrorText(err, "No se han podido comprobar las conexiones."), true);
        resolve(false);
      }
    };
    window.addEventListener("focus", onReturn);
  };

  const [isSavingRawContent, setIsSavingRawContent] = useState(false);
  const handleSaveRawContent = async () => {
    const pieceId = localContentDetail?.id || generatedPieceId;
    if (!pieceId || rawContent === rawContentBaseline) return;
    setIsSavingRawContent(true);
    try {
      await apiClient.patch(`/content-generation/pieces/${pieceId}/`, { raw_content: rawContent });
      setRawContentBaseline(rawContent);
      setLocalContentDetail((prev) => (prev ? { ...prev, raw_content: rawContent } : prev));
      showPopup("Texto base guardado");
    } catch (err) {
      console.error("Error saving base text:", err);
      showPopup(err.response?.data?.detail || "No se pudo guardar el texto base.", true);
    } finally {
      setIsSavingRawContent(false);
    }
  };

  const refreshPieceDetail = async () => {
    const pieceId = localContentDetail?.id;
    if (!pieceId) return;
    try {
      const response = await apiClient.get(`/content-generation/pieces/${pieceId}/`);
      setLocalContentDetail(response.data);
      if (generatedAdaptations.length > 0) {
        setGeneratedAdaptations(response.data.platform_adaptations || []);
      }
      onPieceRefreshed?.(response.data);
    } catch (err) {
      console.error("Error refreshing piece:", err);
    }
  };

  const applyAdaptationText = (adaptationId, text) => {
    setDrafts((prev) => ({
      ...prev,
      [adaptationId]: { ...(prev[adaptationId] || {}), text },
    }));
    setLocalContentDetail((prev) =>
      prev
        ? {
          ...prev,
          platform_adaptations: (prev.platform_adaptations || []).map((adaptation) =>
            adaptation.id === adaptationId ? { ...adaptation, text } : adaptation
          ),
        }
        : prev
    );
    if (generatedAdaptations.length > 0) {
      setGeneratedAdaptations((prev) =>
        prev.map((adaptation) => (adaptation.id === adaptationId ? { ...adaptation, text } : adaptation))
      );
    }
  };

  // Last publishing error about a file ({ message, mediaId }): the card marks that image or video.
  const [mediaError, setMediaError] = useState(null);
  const rememberMediaError = (err) => {
    const info = mediaErrorFrom(err);
    if (info) setMediaError(info);
  };

  const handleSaveAdaptationText = async (adaptationId, text, { silent = false } = {}) => {
    if (!onSaveChanges) return null;
    const updated = await onSaveChanges(adaptationId, { text }, { onError: rememberMediaError, silent });
    if (updated === null) return null;
    applyAdaptationText(adaptationId, updated?.text ?? text);
    return updated;
  };

  // Images added from a review card go straight to the server (no draft).
  const handleAddAdaptationImages = async (adaptationId, files) => {
    if (!onSaveChanges || !files?.length) return null;
    const updated = await onSaveChanges(adaptationId, { imagesToAdd: files }, { onError: rememberMediaError });
    if (updated === null) return null;
    const added = updated?._addImages || [];
    const withImages = (adaptation) =>
      adaptation.id === adaptationId ? { ...adaptation, images: [...(adaptation.images || []), ...added] } : adaptation;
    setLocalContentDetail((prev) =>
      prev ? { ...prev, platform_adaptations: (prev.platform_adaptations || []).map(withImages) } : prev
    );
    if (generatedAdaptations.length > 0) setGeneratedAdaptations((prev) => prev.map(withImages));
    return updated;
  };

  const handleRemoveAdaptationImage = async (adaptationId, imageId) => {
    if (!onSaveChanges || imageId == null) return null;
    const updated = await onSaveChanges(adaptationId, { imageIdsToRemove: [imageId] }, { onError: rememberMediaError });
    if (updated === null) return null;
    setMediaError((prev) => (prev?.mediaId === imageId ? null : prev));
    const withoutImage = (adaptation) =>
      adaptation.id === adaptationId
        ? { ...adaptation, images: (adaptation.images || []).filter((image) => image.id !== imageId) }
        : adaptation;
    setLocalContentDetail((prev) =>
      prev ? { ...prev, platform_adaptations: (prev.platform_adaptations || []).map(withoutImage) } : prev
    );
    if (generatedAdaptations.length > 0) setGeneratedAdaptations((prev) => prev.map(withoutImage));
    return updated;
  };

  const updateAdaptation = (adaptationId, update) => {
    const apply = (adaptation) => (adaptation.id === adaptationId ? update(adaptation) : adaptation);
    setLocalContentDetail((prev) =>
      prev ? { ...prev, platform_adaptations: (prev.platform_adaptations || []).map(apply) } : prev
    );
    if (generatedAdaptations.length > 0) setGeneratedAdaptations((prev) => prev.map(apply));
  };

  // Videos go straight to the server from the card, which shows the progress and the errors (it throws).
  const handleAddAdaptationVideo = async (adaptationId, file, { onProgress, signal } = {}) => {
    try {
      const video = await uploadAdaptationVideo(adaptationId, file, { onProgress, signal });
      if (video) {
        updateAdaptation(adaptationId, (adaptation) => ({ ...adaptation, images: [...(adaptation.images || []), video] }));
      }
      // A new video replaces the one at fault; the next scheduling attempt checks again.
      setMediaError(null);
      showPopup("Vídeo subido");
      return video;
    } catch (err) {
      rememberMediaError(err);
      throw err;
    }
  };

  // LinkedIn PDF: the dialog stays open on error (it throws), after the toast says why.
  const handleAddAdaptationDocument = async (adaptationId, file, title) => {
    try {
      const attached = await uploadAdaptationDocument(adaptationId, file, title);
      updateAdaptation(adaptationId, (adaptation) => ({
        ...adaptation,
        images: [...(adaptation.images || []), attached],
      }));
      setMediaError(null);
      showPopup("Documento adjuntado");
      return attached;
    } catch (err) {
      rememberMediaError(err);
      showPopup(documentUploadErrorText(err), true);
      throw err;
    }
  };

  // The card keeps the crop dialog open on error (it throws), after the toast says why.
  const handleCropAdaptationImage = async (adaptationId, imageId, box) => {
    try {
      const saved = await cropAdaptationImage(imageId, box);
      updateAdaptation(adaptationId, (adaptation) => ({
        ...adaptation,
        images: adaptation.images.map((item) => (item.id === saved.id ? saved : item)),
      }));
      setMediaError((prev) => (prev?.mediaId === imageId ? null : prev));
      showPopup("Imagen recortada");
      return saved;
    } catch (err) {
      showPopup(publishingErrorText(err, "No se ha podido recortar la imagen."), true);
      throw err;
    }
  };

  const handleChangeAdaptationDocumentTitle = async (adaptationId, title) => {
    try {
      const pdf = detailAdaptations.find((adaptation) => adaptation.id === adaptationId).images.find(isDocumentMedia);
      const saved = await updateDocumentTitle(pdf.id, title);
      updateAdaptation(adaptationId, (adaptation) => ({
        ...adaptation,
        images: adaptation.images.map((item) => (item.id === saved.id ? saved : item)),
      }));
      showPopup("Título del documento guardado");
    } catch (err) {
      showPopup(publishingErrorText(err, "No se ha podido guardar el título del documento."), true);
      throw err;
    }
  };

  // Waits for the server (and bundle.social when scheduled) before showing the new format.
  const handleChangeAdaptationFormat = async (adaptationId, format) => {
    const data = await updateAdaptationFormat(adaptationId, format);
    const saved = data?.format || format;
    updateAdaptation(adaptationId, (adaptation) => ({ ...adaptation, format: saved }));
    setMediaError(null);
    const platform = detailAdaptations.find((adaptation) => adaptation.id === adaptationId)?.platform;
    showPopup(`Formato cambiado a «${formatLabel(platform, saved)}»`);
  };

  // Optimista, como el formato: si falla, vuelve al valor anterior.
  const handleChangeAdaptationCallToAction = async (adaptationId, callToAction) => {
    const previous = detailAdaptations.find((adaptation) => adaptation.id === adaptationId);
    const withCallToAction = ({ type, url }) => (adaptation) => ({
      ...adaptation,
      call_to_action: type,
      call_to_action_url: url,
    });
    updateAdaptation(adaptationId, withCallToAction(callToAction));
    try {
      await updateAdaptationCallToAction(adaptationId, callToAction);
    } catch (err) {
      updateAdaptation(adaptationId, withCallToAction(callToActionOf(previous)));
      throw err;
    }
  };


  const applyPieceUpdate = (piece) => {
    setLocalContentDetail(piece);
    onPieceRefreshed?.(piece);
  };

  const handleAddManualAdaptation = async (platform, text, files, { format = "post", silent = false } = {}) => {
    let added = null;
    try {
      const formData = new FormData();
      formData.append("platform", platform);
      formData.append("text", text);
      files.forEach((file) => formData.append("images", file));
      const { data } = await apiClient.post(
        `/content-generation/pieces/${localContentDetail.id}/add_adaptation/`,
        formData,
        { headers: { "Content-Type": "multipart/form-data" } }
      );
      applyPieceUpdate(data);
      added = (data.platform_adaptations || []).find((item) => item.platform === platform);
      if (added) handleSelectAdaptation(added.id);
    } catch (err) {
      console.error("Error adding adaptation:", err);
      showPopup(err.response?.data?.error || "No se ha podido añadir la red.", true);
      return null;
    }
    if (!added) return null;

    // `add_adaptation` doesn't take the format: set it once the network exists.
    if (format !== "post") {
      try {
        await updateAdaptationFormat(added.id, format);
        updateAdaptation(added.id, (adaptation) => ({ ...adaptation, format }));
      } catch (err) {
        showPopup(`Red añadida, pero ${publishingErrorText(err, "no se ha podido guardar el formato")}.`, true);
        return added;
      }
    }
    if (!silent) showPopup("Red añadida");
    return added;
  };

  const handleRemoveAdaptation = async (adaptationId) => {
    try {
      const { data } = await apiClient.post(
        `/content-generation/pieces/${localContentDetail.id}/remove_adaptation/`,
        { adaptation: adaptationId }
      );
      applyPieceUpdate(data);
      showPopup("Red quitada");
    } catch (err) {
      console.error("Error removing adaptation:", err);
      showPopup(err.response?.data?.error || "No se ha podido quitar la red.", true);
    }
  };

  const handleWorkflowSelectAdaptation = (adaptationId) => {
    handleSelectAdaptation(adaptationId);
  };

  const handleEditAdaptationFromWorkflow = (adaptationId) => {
    handleSelectAdaptation(adaptationId);
    goToStep("generation");
  };

  // Shared by step 2 (review) and step 1 once the piece has adaptations.
  const reviewViewProps = {
    piece: localContentDetail,
    adaptations: detailAdaptations,
    selectedAdaptationId: selectedAdaptation?.id,
    onSelectAdaptation: handleWorkflowSelectAdaptation,
    messages: tutorialWorkflow?.messages ?? conversationMessages,
    tutorialVersions: tutorialWorkflow?.versions ?? null,
    isClient: isClientView,
    isBusy: isSaving,
    versionsRefreshKey: combinedRefreshKey,
    onApprove: () => setShowApproveDialog(true),
    onSendToClient: !isClientView ? () => setShowSendToClientDialog(true) : undefined,
    onEditAdaptation: isPastGeneration ? undefined : handleEditAdaptationFromWorkflow,
    onGoToScheduling: () => goToStep("scheduling"),
    onUploadingChange: setIsUploadingMedia,
    onIterate:
      canIterate && !isReadOnly && !isClientView
        ? (adaptationId) => {
            handleSelectAdaptation(adaptationId);
            setShowIterateDialog(true);
          }
        : null,
    iteratingAdaptationId,
    onSaveText: !isReadOnly ? handleSaveAdaptationText : undefined,
    onAddImages: !isReadOnly ? handleAddAdaptationImages : undefined,
    onRemoveImage: !isReadOnly ? handleRemoveAdaptationImage : undefined,
    onCropImage: !isReadOnly && !isClientView ? handleCropAdaptationImage : undefined,
    onAddVideo: !isReadOnly && !isClientView ? handleAddAdaptationVideo : undefined,
    onConnectNetwork: !isClientView && workflowClientId ? handleConnectNetwork : null,
    onChangeFormat: !isReadOnly && !isClientView ? handleChangeAdaptationFormat : undefined,
    onChangeCallToAction: !isReadOnly && !isClientView ? handleChangeAdaptationCallToAction : undefined,
    onAddDocument: !isReadOnly && !isClientView ? handleAddAdaptationDocument : undefined,
    onChangeDocumentTitle: !isReadOnly && !isClientView ? handleChangeAdaptationDocumentTitle : undefined,
    // Any piece, manual or generated; the backend refuses published or scheduled networks.
    onRemoveAdaptation: !isReadOnly && !isClientView ? handleRemoveAdaptation : null,
    mediaError,
    onMessagesChanged: handleConversationRefresh,
    onOpenImage: setPreviewImageUrl,
    notify: (message, isError = false) => showPopup(message, isError),
  };

  // Handle revert to previous version
  const handleRevert = async () => {
    if (!selectedAdaptation || isReverting || versions.length < 2) return;

    setIsReverting(true);
    try {
      const response = await apiClient.post(
        `/content-generation/adaptations/${selectedAdaptation.id}/revert/`
      );

      // Update the draft with the reverted text
      if (response.data?.text) {
        setDrafts((prev) => ({
          ...prev,
          [selectedAdaptation.id]: {
            ...prev[selectedAdaptation.id],
            text: response.data.text,
          },
        }));
      }

      // Refresh versions to show the new revert version
      refetchVersions();
      showPopup("Contenido revertido a la versión anterior");
    } catch (err) {
      console.error("Error reverting version:", err);
      showPopup(
        err.response?.data?.error || "Error al revertir a la versión anterior.",
        true
      );
    } finally {
      setIsReverting(false);
    }
  };

  const handleTextChange = (value) => {
    if (isReadOnly) return;
    if (!selectedAdaptation) return;
    setDrafts((prev) => ({
      ...prev,
      [selectedAdaptation.id]: {
        ...prev[selectedAdaptation.id],
        text: value,
      },
    }));
  };

  const handleSelectAdaptation = (adaptationId) => {
    setLocalSelectedAdaptationId(adaptationId);
  };

  const handleRemoveImage = (imageId) => {
    if (isReadOnly) return;
    if (!selectedAdaptation) return;
    setDrafts((prev) => {
      const current = prev[selectedAdaptation.id] || {};
      const currentPending = current.pendingImages || [];
      const currentRemoveIds = current.imageIdsToRemove || [];

      // Check if it's a pending (not-yet-saved) image
      const isPending = currentPending.some((img) => img.id === imageId);
      if (isPending) {
        // Revoke the object URL to free memory
        const pendingImg = currentPending.find((img) => img.id === imageId);
        if (pendingImg?.url) URL.revokeObjectURL(pendingImg.url);
        return {
          ...prev,
          [selectedAdaptation.id]: {
            ...current,
            pendingImages: currentPending.filter((img) => img.id !== imageId),
          },
        };
      }

      // It's a persisted image — mark for removal
      return {
        ...prev,
        [selectedAdaptation.id]: {
          ...current,
          imageIdsToRemove: [...new Set([...currentRemoveIds, imageId])],
        },
      };
    });
  };

  const handleOpenIterateDialog = () => {
    if (isReadOnly) return;
    if (!selectedAdaptation) return;
    setShowIterateDialog(true);
  };

  const handleIterateSubmit = async () => {
    if (isReadOnly) return;
    if (!selectedAdaptation || !onIterateAdaptation) return;
    const directive = iteratePrompt.trim();
    if (!directive) return;
    setShowIterateDialog(false);
    setIteratePrompt("");

    // In create mode, pass the generatedPieceId so the parent can fetch the correct piece
    const pieceIdForIterate = mode === "create" ? generatedPieceId : null;
    const updated = await onIterateAdaptation(selectedAdaptation.id, directive, pieceIdForIterate);
    if (!updated?.text) return;

    // Update drafts with new text
    setDrafts((prev) => ({
      ...prev,
      [selectedAdaptation.id]: {
        ...prev[selectedAdaptation.id],
        text: updated.text,
      },
    }));

    // In create mode, also update generatedAdaptations so the UI reflects the new content
    if (mode === "create" && generatedAdaptations.length > 0) {
      setGeneratedAdaptations((prev) =>
        prev.map((adaptation) =>
          adaptation.id === selectedAdaptation.id
            ? { ...adaptation, text: updated.text }
            : adaptation
        )
      );
    }
  };

  // Handle raw content iteration (similar to adaptation iteration but for raw_content)
  const handleIterateRawSubmit = async () => {
    const directive = iterateRawPrompt.trim();
    if (!directive || !generatedPieceId) return;

    setShowIterateRawDialog(false);
    setIterateRawPrompt("");
    setIsIteratingRawContent(true);

    try {
      // Call iterate endpoint for the piece
      const response = await apiClient.post(
        `/content-generation/pieces/${generatedPieceId}/iterate/`,
        { improvement_directive: directive }
      );
      const taskId = response?.data?.task_id;
      if (!taskId) {
        throw new Error("No se pudo iniciar la regeneración.");
      }

      // Poll for completion
      let attempts = 0;
      const maxAttempts = 30;
      while (attempts < maxAttempts) {
        const statusResponse = await apiClient.get(
          `/content-generation/task-status/${taskId}/`
        );
        const { status, error } = statusResponse.data || {};

        if (status === "SUCCESS") {
          break;
        }
        if (status === "FAILURE") {
          throw new Error(error || "Error al regenerar el contenido con IA.");
        }

        attempts += 1;
        await new Promise((resolve) => setTimeout(resolve, 1000));
      }

      if (attempts >= maxAttempts) {
        throw new Error("Timeout: La regeneración está tardando demasiado.");
      }

      // Fetch the updated piece
      const pieceResponse = await apiClient.get(
        `/content-generation/pieces/${generatedPieceId}/`
      );
      const updatedPiece = pieceResponse.data;

      // Update raw content with the new value from backend
      if (updatedPiece.raw_content) {
        setRawContent(updatedPiece.raw_content);
        setRawContentBaseline(updatedPiece.raw_content);
      }

      showPopup("Contenido regenerado con éxito");
    } catch (error) {
      console.error("Error iterating raw content:", error);
      showPopup(
        error?.response?.data?.error ||
        error?.message ||
        "Error al regenerar el contenido con IA.",
        true
      );
    } finally {
      setIsIteratingRawContent(false);
    }
  };

  const handleSaveContentChanges = async () => {
    if (isReadOnly) return;
    if (!selectedAdaptation || !onSaveChanges) return;
    if (!hasContentChanges) return;
    const changes = {};
    if (hasTextChanges) {
      changes.text = draftText;
    }
    if (pendingImages.length > 0) {
      changes.imagesToAdd = pendingImages.map((img) => img.file);
    }
    if (imageIdsToRemove.size > 0) {
      changes.imageIdsToRemove = [...imageIdsToRemove];
    }
    if (!Object.keys(changes).length) return;

    setIsSaving(true);
    try {
      const updated = await onSaveChanges(selectedAdaptation.id, changes);
      if (updated === null) return;

      // Merge images from server response into selectedAdaptation
      const mergeImages = (baseImages) => {
        let imgs = [...(baseImages || [])];
        if (updated?._addImages?.length) imgs = [...imgs, ...updated._addImages];
        if (updated?._removeImageIds?.length) {
          const removeSet = new Set(updated._removeImageIds);
          imgs = imgs.filter((img) => !removeSet.has(img.id));
        }
        return imgs;
      };

      // Clear pending images and removal queue from draft
      setDrafts((prev) => {
        const currentDraft = prev[selectedAdaptation.id] || {};
        return {
          ...prev,
          [selectedAdaptation.id]: {
            ...currentDraft,
            text: updated?.text ?? changes.text ?? currentDraft.text ?? draftText,
            pendingImages: [],
            imageIdsToRemove: [],
          },
        };
      });
      if (mode === "create" && generatedAdaptations.length > 0) {
        setGeneratedAdaptations((prev) =>
          prev.map((adaptation) => {
            if (adaptation.id !== selectedAdaptation.id) return adaptation;
            const next = { ...adaptation };
            if (hasTextChanges) next.text = updated?.text ?? draftText;
            next.images = mergeImages(adaptation.images);
            return next;
          })
        );
      }
      // In edit mode, update localContentDetail so that originalImages reflects the changes
      if (mode !== "create") {
        setLocalContentDetail((prev) => {
          if (!prev) return prev;
          return {
            ...prev,
            platform_adaptations: (prev.platform_adaptations || []).map((adaptation) => {
              if (adaptation.id !== selectedAdaptation.id) return adaptation;
              const next = { ...adaptation };
              if (hasTextChanges) next.text = updated?.text ?? draftText;
              next.images = mergeImages(adaptation.images);
              return next;
            }),
          };
        });
      }
      // No automatic version refresh here
    } finally {
      setIsSaving(false);
    }
  };

  const handleImageUpload = (file) => {
    if (isReadOnly) return "";
    if (!selectedAdaptation || !file) return "";
    const previewUrl = URL.createObjectURL(file);
    const tempId = `pending-${Date.now()}-${Math.random()}`;
    setDrafts((prev) => {
      const current = prev[selectedAdaptation.id] || {};
      const currentPending = current.pendingImages || [];
      return {
        ...prev,
        [selectedAdaptation.id]: {
          ...current,
          pendingImages: [...currentPending, { id: tempId, url: previewUrl, file }],
        },
      };
    });
    return previewUrl;
  };

  const savePendingAdaptationChanges = async () => {
    if (!detailAdaptations.length || !onSaveChanges) return;
    const savePromises = detailAdaptations.map(async (adaptation) => {
      const draft = drafts[adaptation.id];
      if (!draft) return;

      const changes = {};
      const originalText = adaptation.text || "";
      const draftText = draft.text ?? originalText;
      const pendingImgs = draft.pendingImages || [];
      const removeIds = draft.imageIdsToRemove || [];

      if (normalizeMarkdownForCompare(draftText) !== normalizeMarkdownForCompare(originalText)) {
        changes.text = draftText;
      }
      // The date belongs to the piece now (Programación); adaptations no longer store it.
      if (pendingImgs.length > 0) {
        changes.imagesToAdd = pendingImgs.map((img) => img.file);
      }
      if (removeIds.length > 0) {
        changes.imageIdsToRemove = removeIds;
      }

      if (Object.keys(changes).length > 0) {
        await onSaveChanges(adaptation.id, changes);
      }
    });

    await Promise.all(savePromises);
  };

  // Handle sending content to client (draft -> review)
  const handleSendToClient = async () => {
    const pieceId = mode === "create" ? generatedPieceId : localContentDetail?.id;
    if (!pieceId) {
      showPopup("No hay contenido para enviar.", true);
      return;
    }

    // If piece belongs to a group, validate all pieces are ready before calling API
    if (pieceGroupId) {
      if (groupAllPiecesReady === false) {
        setShowSendToClientDialog(false);
        showPopup(
          "Algunos contenidos del grupo no tienen adaptaciones de plataforma. Genera las adaptaciones de todos los contenidos del grupo antes de enviar.",
          true,
          6000
        );
        return;
      }
    }

    setIsSaving(true);
    setShowSendToClientDialog(false);

    try {
      // First, save all pending changes for all adaptations
      await savePendingAdaptationChanges();

      // If piece belongs to a group, use the group endpoint
      if (pieceGroupId) {
        await apiClient.post(`/content-generation/groups/${pieceGroupId}/send_to_review/`);
        showPopup(`Grupo "${pieceGroupName || "Grupo"}" enviado a revisión correctamente`);
        setLocalContentDetail((prev) =>
          prev ? { ...prev, status: "client_review" } : prev
        );
      } else {
        await apiClient.post(`/content-generation/pieces/${pieceId}/send_to_review/`);
        showPopup("Contenido enviado a revisión correctamente");
        setLocalContentDetail((prev) =>
          prev ? { ...prev, status: "client_review" } : prev
        );
      }
    } catch (err) {
      console.error("Error sending to client:", err);
      showPopup(err.response?.data?.error || "Error al enviar a revisión.", true);
    } finally {
      setIsSaving(false);
      refreshPieceDetail();
    }
  };

  // Handle approving content (review -> accepted)
  const handleApproveContent = async () => {
    const pieceId = localContentDetail?.id || generatedPieceId;
    if (!pieceId) return;

    // If piece belongs to a group, validate all pieces are ready before calling API
    if (pieceGroupId) {
      if (groupAllPiecesReady === false) {
        setShowApproveDialog(false);
        showPopup(
          "Algunos contenidos del grupo no tienen adaptaciones de plataforma. Genera las adaptaciones de todos los contenidos del grupo antes de aprobar.",
          true,
          6000
        );
        return;
      }
    }

    setIsSaving(true);
    setShowApproveDialog(false);
    try {
      // First, save all pending changes for all adaptations
      await savePendingAdaptationChanges();

      // If piece belongs to a group, use the group endpoint
      if (pieceGroupId) {
        await apiClient.post(`/content-generation/groups/${pieceGroupId}/approve/`);
        showPopup(`Grupo "${pieceGroupName || "Grupo"}" marcado como aceptado correctamente`);
        setLocalContentDetail((prev) =>
          prev ? { ...prev, status: "client_approved" } : prev
        );
      } else {
        await apiClient.post(`/content-generation/pieces/${pieceId}/approve/`);
        showPopup("Contenido marcado como aceptado correctamente");
        setLocalContentDetail((prev) =>
          prev ? { ...prev, status: "client_approved" } : prev
        );
      }
    } catch (err) {
      console.error("Error approving content:", err);
      showPopup(
        err.response?.data?.error ||
        "Error al marcar como aceptado. Inténtalo de nuevo.",
        true
      );
    } finally {
      setIsSaving(false);
      refreshPieceDetail();
    }
  };

  // Render creation mode
  // Loader shown while the AI generates the base text or the adaptations.
  const renderGenerationLoader = (label, sx = {}) => (
    <Box
      sx={{
        display: "flex",
        flexDirection: "column",
        alignItems: "center",
        justifyContent: "center",
        gap: 2,
        p: 4,
        minHeight: 300,
        textAlign: "center",
        ...sx,
      }}
    >
      <CircularProgress size={40} sx={{ color: colors.primary.main }} />
      <Typography variant="body1" sx={{ color: neutrals.gray[600] }}>
        {label}
      </Typography>
      <Typography variant="body2" sx={{ color: neutrals.gray[500] }}>
        No abandones esta ventana mientras se genera el contenido.
      </Typography>
    </Box>
  );

  const renderCreationMode = () => {
    // Input step - ask for topic, language, and platforms
    if (creationStep === "input") {
      return (
        <Box
          data-tutorial="cpm-input-form"
          sx={{
            display: "flex",
            flexDirection: "column",
            gap: 3,
            p: 3,
          }}
        >
          {/* Header */}
          <Box sx={{ display: "flex", flexDirection: "column", alignItems: "center", textAlign: "center" }}>
            <Sparkles size={40} color={colors.primary.main} style={{ marginBottom: 8 }} />
            <Typography variant="h6" sx={{ fontWeight: 600 }}>
              Crear nuevo contenido
            </Typography>
            <Typography variant="body2" sx={{ color: neutrals.gray[500], mt: 0.5 }}>
              Introduce un título/temática y selecciona las opciones de generación
            </Typography>
          </Box>

          {/* Topic input */}
          <Box sx={{ display: "flex", flexDirection: "column", gap: 1 }}>
            <Label htmlFor="content-topic">
              Temática del contenido <span style={{ color: colors.error?.main || "#d32f2f" }}>*</span>
            </Label>
            <CustomTextField
              id="content-topic"
              value={topic}
              onChange={(e) => setTopic(e.target.value)}
              placeholder="Ej: Promoción de Navidad con 40% de descuento en todos los productos..."
              multiline
              rows={3}
            />

          </Box>

          {/* Language and Communication style selectors - all in one row */}
          <Box sx={{ display: "grid", gridTemplateColumns: "repeat(3, 1fr)", gap: 2 }}>
            <Box sx={{ display: "flex", flexDirection: "column", gap: 1 }}>
              <Label htmlFor="content-language">
                Idioma <span style={{ color: colors.error?.main || "#d32f2f" }}>*</span>
              </Label>
              <CustomSelector
                value={selectedLanguage}
                onChange={(value) => setSelectedLanguage(value)}
                options={languageOptions}
                placeholder="Seleccionar"
              />
            </Box>
            <Box sx={{ display: "flex", flexDirection: "column", gap: 1 }}>
              <Label htmlFor="content-tone">
                Tono <span style={{ color: colors.error?.main || "#d32f2f" }}>*</span>
              </Label>
              <CustomSelector
                value={selectedTone}
                onChange={(value) => setSelectedTone(value)}
                options={toneOptions}
                placeholder="Selecciona un tono o especifica otro"
                searchable
                freeSolo
              />
            </Box>
            <Box sx={{ display: "flex", flexDirection: "column", gap: 1 }}>
              <Label htmlFor="content-language-type">
                Tipo de Lenguaje <span style={{ color: colors.error?.main || "#d32f2f" }}>*</span>
              </Label>
              <CustomSelector
                value={selectedLanguageType}
                onChange={(value) => setSelectedLanguageType(value)}
                options={languageTypeOptions}
                placeholder="Selecciona un tipo o especifica otro"
                searchable
                freeSolo
              />
            </Box>
          </Box>

          {/* Action buttons */}
          <Box sx={{ display: "flex", justifyContent: "flex-end", gap: 1.5, pt: 2, borderTop: `1px solid ${neutrals.gray[200]}` }}>
            <Button
              onClick={handleGenerateContent}
              disabled={!topic.trim() || !configId}
            >
              <Sparkles size={16} style={{ marginRight: 6 }} />
              Generar contenido
            </Button>
          </Box>
        </Box>
      );
    }

    // No content step - piece exists but has no raw content yet (Case 3)
    if (creationStep === "no_content") {
      // Check if there's an error message (generation failed)
      const errorMessage = pieceErrorMessage || localContentDetail?.error_message;
      const hasError = !!errorMessage;

      return (
        <Box
          sx={{
            display: "flex",
            flexDirection: "column",
            alignItems: "center",
            justifyContent: "center",
            gap: 2,
            p: 4,
            minHeight: 300,
            textAlign: "center",
          }}
        >
          {hasError ? (
            // Error state - generation failed
            <>
              <X size={48} color={colors.status?.error || "#ef4444"} />
              <Typography variant="h6" sx={{ fontWeight: 600, color: colors.status?.error || "#ef4444" }}>
                Error en la generación
              </Typography>
              <Typography variant="body2" sx={{ color: neutrals.gray[500], maxWidth: 400 }}>
                Algo ha salido mal durante la generación de este contenido. Por favor, elimine este contenido y póngase en contacto con los administradores si el problema persiste.
              </Typography>
              <Box sx={{ display: "flex", gap: 1.5, mt: 2 }}>
                <Button variant="outline" onClick={() => onClose?.({ skipRefresh: false })}>
                  Cerrar
                </Button>
              </Box>
            </>
          ) : (
            // Generating state - waiting for raw content
            <>
              <CircularProgress size={40} sx={{ color: colors.primary.main }} />
              <Typography variant="h6" sx={{ fontWeight: 600 }}>
                {topic || pieceTitle || "Generando contenido..."}
              </Typography>
              <Typography variant="body2" sx={{ color: neutrals.gray[500], maxWidth: 400 }}>
                El contenido se está generando, por favor cierre la ventana y espere unos segundos.
              </Typography>
              <Box sx={{ display: "flex", gap: 1.5, mt: 2 }}>
                <Button variant="outline" onClick={() => onClose?.({ skipRefresh: false })}>
                  Cerrar
                </Button>
              </Box>
            </>
          )}
        </Box>
      );
    }

    // Generating raw content step
    if (creationStep === "generating_raw") {
      return renderGenerationLoader("Generando contenido base, por favor espera...");
    }

    // Raw content ready step - show in MarkdownEditor
    if (creationStep === "raw_ready" && isClientView) {
      return (
        <Box
          sx={{
            display: "flex",
            flexDirection: "column",
            alignItems: "center",
            justifyContent: "center",
            gap: 1,
            p: 4,
            minHeight: 300,
            textAlign: "center",
          }}
        >
          <FileText size={32} color={neutrals.gray[400]} />
          <Typography variant="h6" sx={{ fontWeight: 600 }}>
            Este contenido no tiene información
          </Typography>
          <Typography variant="body2" sx={{ color: neutrals.gray[500] }}>
            Por favor, notifique a su consultor del problema.
          </Typography>
        </Box>
      );
    }

    // Base text only: it is shown in step 1 of the workflow once the piece detail is loaded.
    if (creationStep === "raw_ready" || creationStep === "generating_adaptations") {
      return (
        <Box sx={{ display: "flex", alignItems: "center", justifyContent: "center", p: 4, minHeight: 300 }}>
          <CircularProgress size={32} sx={{ color: colors.primary.main }} />
        </Box>
      );
    }

    return null;
  };

  return (
    <>
      <SuccessPopup
        message={popupMessage}
        isOpen={popupOpen}
        onClose={() => setPopupOpen(false)}
        error={popupIsError}
        autoHideDuration={popupDuration}
      />

      <Box
        translate="no"
        sx={{
          display: "flex",
          flexDirection: "column",
          flex: 1,
          minHeight: 0,
          overflow: "hidden",
          // Frameless: no inner scroll, the whole page scrolls.
          ...(isFrameless
            ? { minHeight: "auto", overflow: "visible" }
            : {
                backgroundColor: "#fff",
                borderRadius: { xs: 0, md: "16px" },
                border: { xs: "none", md: `1px solid ${neutrals.gray[200]}` },
              }),
        }}
      >
          {/* Header */}
          {!externalHeader && (
          <Box
            sx={{
              display: "flex",
              alignItems: "center",
              justifyContent: "space-between",
              padding: { xs: "12px 12px 10px 8px", md: "16px 24px 14px 16px" },
              borderBottom: `1px solid ${neutrals.gray[200]}`,
              flexShrink: 0,
            }}
          >
            <Box sx={{ display: "flex", alignItems: "center", gap: 1, flexWrap: "wrap", minWidth: 0 }}>
              <IconButton onClick={onClose} aria-label="Volver" size="small">
                <ArrowLeft size={20} />
              </IconButton>
              <Typography variant="h5" sx={{ fontWeight: 600 }}>
                {(mode === "edit" || (mode === "create" && localContentDetail?.title))
                  ? localContentDetail?.title || "Contenido"
                  : (creationStep === "raw_ready" || creationStep === "generating_adaptations")
                    ? "Generar adaptaciones"
                    : "Crear contenido"}
              </Typography>
              {mode === "edit" && hasResolvedStatus && (
                <Badge color={getPieceDisplayStatus(localContentDetail).color}>
                  {getPieceDisplayStatus(localContentDetail).label}
                </Badge>
              )}
              {isTutorialActive && (
                <Box
                  sx={{
                    px: 1.5,
                    py: 0.4,
                    borderRadius: "6px",
                    backgroundColor: alpha(muiTheme.palette.info.main, 0.1),
                    border: `1px solid ${alpha(muiTheme.palette.info.main, 0.25)}`,
                  }}
                >
                  <Typography variant="caption" sx={{ color: "info.main", fontWeight: 500 }}>
                    Modo guiado activo
                  </Typography>
                </Box>
              )}
            </Box>
            <Box sx={{ display: "flex", alignItems: "center", gap: 1 }}>
              {isClientView && mode !== "create" && canShowClientAcceptAction && isMobile && activeWorkflowView === "generation" && (
                <Button
                  onClick={() => setShowApproveDialog(true)}
                  disabled={statusActionDisabled}
                  size="sm"
                >
                  <Check size={16} />
                  Aceptar contenido
                </Button>
              )}
            </Box>
          </Box>
          )}

          {showWorkflow && !isClientView && (
            <Box
              data-tutorial="cpm-workflow-stepper"
              sx={isFrameless ? { flexShrink: 0 } : { px: { xs: 1.5, md: 3 }, pt: { xs: 1.5, md: 2 }, flexShrink: 0 }}
            >
              <WorkflowStepper
                steps={workflow.steps}
                selectedKey={activeWorkflowView}
                onSelect={goToStep}
              />
            </Box>
          )}

          {/* Content - different based on mode */}
          {/* Use renderCreationMode for creation steps or special edit mode steps (raw_ready, no_content) */}
          <Box
            sx={{
              flex: 1,
              minHeight: 0,
              overflow: isFrameless ? "visible" : "auto",
              display: "flex",
              flexDirection: "column",
            }}
          >
            {showWorkflow && activeWorkflowView !== "generation" ? (
              <Box sx={isFrameless ? { pt: isClientView ? 0 : 2, flex: 1 } : { p: { xs: 1.5, md: 3 }, pt: { xs: 2, md: 2.5 }, backgroundColor: "#F7F8FA", flex: 1 }}>
                {activeWorkflowView === "review" && (
                  <ReviewView {...reviewViewProps} onAddAdaptation={canAddNetworks ? handleAddManualAdaptation : null} />
                )}
                {activeWorkflowView === "scheduling" && !isSchedulingLocked && (
                  <SchedulingView
                    piece={localContentDetail}
                    adaptations={detailAdaptations}
                    preview={<ReviewView {...reviewViewProps} showSidebar={false} hideMainAction />}
                    isClient={isClientView}
                    clientId={workflowClientId}
                    tutorialSocial={tutorialWorkflow?.social ?? null}
                    onPieceChanged={refreshPieceDetail}
                    onPublishError={(err) => (err ? rememberMediaError(err) : setMediaError(null))}
                    notify={(message, isError = false) => showPopup(message, isError)}
                  />
                )}
              </Box>
            ) : showWorkflow && creationStep === "generating_adaptations" ? (
              renderGenerationLoader("Generando adaptaciones para cada plataforma...", {
                flex: 1,
                ...(isFrameless
                  ? { mt: 2, backgroundColor: "#fff", borderRadius: "16px", border: `1px solid ${neutrals.gray[200]}` }
                  : {}),
              })
            ) : showWorkflow && isBaseTextStep ? (
              <BaseTextPanel
                // Same max width as the stepper above.
                sx={{ ...(isFrameless ? { mt: 2 } : { m: { xs: 1.5, md: 3 } }), maxWidth: { md: 1440 } }}
                value={rawContent}
                onChange={setRawContent}
                onIterate={() => setShowIterateRawDialog(true)}
                isIterating={isIteratingRawContent}
                highlightIterate={isTutorialActive}
                channels={ADAPTATION_PLATFORMS}
                selectedChannels={selectedPlatforms}
                clientId={workflowClientId}
                onToggleChannel={togglePlatform}
                onGenerateAdaptations={handleConfirmRaw}
              />
            ) : showWorkflow && !isClientView && detailAdaptations.length > 0 ? (
              <Box sx={isFrameless ? { pt: 2, flex: 1 } : { p: { xs: 1.5, md: 3 }, pt: { xs: 2, md: 2.5 }, backgroundColor: "#F7F8FA", flex: 1 }}>
                <ReviewView
                  {...reviewViewProps}
                  showSidebar={false}
                  onAddAdaptation={canAddNetworks ? handleAddManualAdaptation : null}
                />
              </Box>
            ) : (mode === "create"
              ? (creationStep === "select_type" ||
                creationStep === "input" ||
                creationStep === "campaign_config" ||
                creationStep === "generating_raw" ||
                creationStep === "raw_ready" ||
                creationStep === "generating_adaptations")
              : (creationStep === "no_content" || creationStep === "raw_ready" || creationStep === "generating_raw" || creationStep === "generating_adaptations")) ? (
              renderCreationMode()
            ) : (
              <>
              {showWorkflow && !isClientView && (
                <BaseTextPanel
                  sx={isFrameless ? { mt: 2 } : { mx: { xs: 1.5, md: 3 }, mt: { xs: 1.5, md: 2 } }}
                  value={rawContent}
                  onChange={setRawContent}
                  readOnly={isPastGeneration}
                  onIterate={() => setShowIterateRawDialog(true)}
                  isIterating={isIteratingRawContent}
                  isDirty={rawContent !== rawContentBaseline}
                  onSave={handleSaveRawContent}
                  onDiscard={() => setRawContent(rawContentBaseline)}
                  isSaving={isSavingRawContent}
                  collapsible
                />
              )}
              <Box
                sx={{
                  padding: { xs: "12px 12px 16px 12px", md: "20px 24px 24px 24px" },
                  display: "flex",
                  flexDirection: { xs: "column", lg: "row" },
                  gap: { xs: 2, md: 3 },
                  overflow: { xs: "visible", lg: "hidden" },
                  flex: 1,
                  // With the base text above, keep the editor usable and let the page scroll.
                  minHeight: showWorkflow && !isClientView ? { xs: 0, lg: 560 } : 0,
                  ...(isFrameless
                    ? {
                        mt: 2,
                        backgroundColor: "#fff",
                        borderRadius: "16px",
                        border: `1px solid ${neutrals.gray[200]}`,
                      }
                    : {}),
                }}
              >
                {isLoading ? null : (
                  <>
                    {/* Left side - 60% (or 100% in client view) - Editor */}
                    <Box
                      sx={{
                        flex: isClientView ? "1" : "1 1 0%",
                        display: "flex",
                        flexDirection: "column",
                        gap: 2,
                        overflow: { xs: "visible", lg: "hidden" },
                        minHeight: 0,
                      }}
                    >
                      {isClientView && detailAdaptations.length === 0 ? (
                        <Box sx={{ flex: 1, display: "flex", alignItems: "center", justifyContent: "center", minHeight: 300 }}>
                          <EmptyStateMessage
                            icon={FileX}
                            message="Este contenido aún no tiene adaptaciones para plataformas"
                            iconSize={48}
                          />
                        </Box>
                      ) : (
                        <>
                          <Box
                            data-tutorial={!isClientView ? "cpm-adaptations-editor" : undefined}
                            sx={{
                              display: "flex",
                              flexDirection: "column",
                              gap: 2,
                              flex: 1,
                              minHeight: 0,
                            }}
                          >
                            {!isClientView && (
                              <Box
                                sx={{
                                  display: "flex",
                                  alignItems: "center",
                                  flexWrap: "wrap",
                                  gap: 1.5,
                                }}
                              >
                                <Typography variant="subtitle1" sx={{ fontWeight: 600 }}>
                                  {isReadOnly ? "1. Vista:" : "1. Edición:"}
                                </Typography>
                                <Box sx={{ display: "flex", alignItems: "center", gap: 0.75, flexWrap: "wrap", maxWidth: "100%" }}>
                                  {detailAdaptations.map((adaptation) => {
                                    const { Icon, label: platformName } = getPlatformMeta(adaptation.platform);
                                    const isSelected = adaptation.id === effectiveSelectedAdaptationId;
                                    const platformColor =
                                      platformColors[adaptation.platform] || colors.primary.main;

                                    return (
                                      <Tooltip
                                        key={adaptation.id}
                                        title={platformName}
                                        arrow
                                      >
                                        <IconButton
                                          onClick={() => handleSelectAdaptation(adaptation.id)}
                                          sx={{
                                            width: 32,
                                            height: 32,
                                            padding: "8px",
                                            borderRadius: "8px",
                                            border: `1px solid ${isSelected ? platformColor : neutrals.gray[200]
                                              }`,
                                            backgroundColor: isSelected
                                              ? `${platformColor}1A`
                                              : neutrals.white,
                                          }}
                                        >
                                          <Icon sx={{ fontSize: 15, color: platformColor }} />
                                        </IconButton>
                                      </Tooltip>
                                    );
                                  })}
                                </Box>
                              </Box>
                            )}
                            {isClientView && (
                              <Box sx={{ display: "flex", alignItems: "center", gap: 0.75, flexWrap: "wrap" }}>
                                {detailAdaptations.map((adaptation) => {
                                  const { Icon, label: platformName } = getPlatformMeta(adaptation.platform);
                                  const isSelected = adaptation.id === effectiveSelectedAdaptationId;
                                  const platformColor =
                                    platformColors[adaptation.platform] || colors.primary.main;

                                  return (
                                    <Tooltip
                                      key={adaptation.id}
                                      title={platformName}
                                      arrow
                                    >
                                      <IconButton
                                        onClick={() => handleSelectAdaptation(adaptation.id)}
                                        sx={{
                                          width: 32,
                                          height: 32,
                                          padding: "8px",
                                          borderRadius: "8px",
                                          border: `1px solid ${isSelected ? platformColor : neutrals.gray[200]
                                            }`,
                                          backgroundColor: isSelected
                                            ? `${platformColor}1A`
                                            : neutrals.white,
                                        }}
                                      >
                                        <Icon sx={{ fontSize: 15, color: platformColor }} />
                                      </IconButton>
                                    </Tooltip>
                                  );
                                })}
                              </Box>
                            )}
                            <Box sx={{ flex: 1, minHeight: 0, display: "flex", flexDirection: "column", overflow: "hidden", width: "100%" }}>
                              {canShowSaveAction && !isClientView && (
                                <UnsavedChangesBar
                                  count={hasContentChanges ? 1 : 0}
                                  saving={isSaving}
                                  onDiscard={discardContentChanges}
                                  onSave={handleSaveContentChanges}
                                />
                              )}
                              <MarkdownEditor
                                key={`edit-adaptation-${selectedAdaptation?.id}`}
                                value={draftText}
                                onChange={handleTextChange}
                                minHeight={isClientView && isMobile ? "180px" : "100%"}
                                maxHeight={isClientView && isMobile ? "52vh" : "100%"}
                                placeholder="Edita el contenido..."
                                allowImages={!isReadOnly}
                                insertImages={false}
                                allowHeadings={selectedAdaptation?.platform === "blog"}
                                onImageUpload={handleImageUpload}
                                onIterate={
                                  canIterate && !isClientView && !isReadOnly
                                    ? handleOpenIterateDialog
                                    : undefined
                                }
                                isIterating={
                                  canIterate && !isClientView && !isReadOnly
                                    ? isIterating
                                    : false
                                }
                                iterateButtonDataTutorial="cpm-iterate-button"
                                highlightIterateButton={isTutorialActive && !isClientView && !isReadOnly}
                                readOnly={isReadOnly}
                                toolbarActions={
                                  ((canShowSaveAction && isClientView) || canShowHistoryAction) ? (
                                    <Box sx={{ display: "flex", alignItems: "center", gap: 1 }}>
                                      {canShowSaveAction && isClientView && (
                                        <Button
                                          onClick={handleSaveContentChanges}
                                          disabled={!canSaveContent}
                                          size="sm"
                                          component="span"
                                          variant="outline"
                                        >
                                          {isSaving ? <CircularProgress size={14} /> : <Save size={16} />}
                                          {isSaving ? "Guardando…" : "Enviar cambios"}
                                        </Button>
                                      )}
                                      {canShowHistoryAction && (
                                        <Button
                                          onClick={() => {
                                            refetchVersions();
                                            setShowVersionHistoryDialog(true);
                                          }}
                                          size="sm"
                                          component="span"
                                          variant="outline"
                                        >
                                          <History size={16} />
                                          Ver historial
                                        </Button>
                                      )}
                                    </Box>
                                  ) : null
                                }
                                attachmentContent={
                                  currentImages.length > 0 ? (
                                    <ImageCarousel
                                      images={currentImages}
                                      onRemove={!isReadOnly ? handleRemoveImage : undefined}
                                      onImageClick={(url) => setPreviewImageUrl(url)}
                                      readOnly={isReadOnly}
                                    />
                                  ) : null
                                }
                              />
                            </Box>
                          </Box>
                          {!isClientView && (
                            <Box
                              sx={{
                                display: "flex",
                                alignItems: "center",
                                justifyContent: "space-between",
                                gap: 1.5,
                              }}
                            >
                              <Box sx={{ display: "flex", alignItems: "center", gap: 1.5 }}>
                                {/* The date is the piece's: it is set and scheduled in Programación. */}
                                <Button
                                  variant="outline"
                                  onClick={() => goToStep("scheduling")}
                                  disabled={!showWorkflow || isSchedulingLocked}
                                >
                                  <Calendar size={16} />
                                  {localContentDetail?.scheduled_at
                                    ? formatScheduleLabel(localContentDetail.scheduled_at)
                                    : "Programación"}
                                </Button>
                              </Box>
                              {canShowStatusAction && (
                                <Button
                                  onClick={
                                    isDraft
                                      ? () => setShowSendToClientDialog(true)
                                      : () => setShowApproveDialog(true)
                                  }
                                  disabled={statusActionDisabled}
                                >
                                  {statusActionIcon}
                                  {statusActionLabel}
                                </Button>
                              )}
                            </Box>
                          )}
                        </>
                      )}
                    </Box>

                    {/* Middle side - conversation */}
                    <Box
                      sx={{
                        flex: "1 1 0%",
                        display: "flex",
                        flexDirection: "column",
                        borderLeft: { xs: "none", lg: `1px solid ${neutrals.gray[200]}` },
                        paddingLeft: { xs: 0, lg: 3 },
                        paddingTop: { xs: 0, lg: 0.5 },
                        overflow: { xs: "visible", lg: "hidden" },
                        minHeight: 0,
                      }}
                    >
                      <Box sx={{ display: "flex", alignItems: "baseline", gap: 1, mb: 1.5, minWidth: 0, minHeight: 32 }}>
                        <Typography variant="subtitle1" sx={{ fontWeight: 600, whiteSpace: "nowrap" }}>
                          2. Conversación:
                        </Typography>
                        <Typography
                          variant="body2"
                          sx={{
                            color: neutrals.gray[600],
                            fontWeight: 600,
                            fontStyle: "italic",
                            whiteSpace: "nowrap",
                            overflow: "hidden",
                            textOverflow: "ellipsis",
                            minWidth: 0,
                          }}
                        >
                          Asociada a todas las plataformas.
                        </Typography>
                      </Box>
                      <Box
                        sx={{
                          flex: 1,
                          minHeight: { xs: "360px", lg: 0 },
                          height: { xs: "52vh", lg: "auto" },
                          border: `1px solid ${neutrals.gray[200]}`,
                          borderRadius: "12px",
                          overflow: "hidden",
                          backgroundColor: neutrals.white,
                        }}
                      >
                        {shouldShowConversationSkeleton ? (
                          <Box sx={{ height: "100%", display: "flex", flexDirection: "column", p: 2, gap: 1.5 }}>
                            <Skeleton variant="rounded" height={18} width="35%" />
                            <Box sx={{ flex: 1, display: "flex", flexDirection: "column", gap: 1.5 }}>
                              <Skeleton variant="rounded" height={68} width="78%" />
                              <Skeleton variant="rounded" height={52} width="58%" sx={{ alignSelf: "flex-end" }} />
                              <Skeleton variant="rounded" height={68} width="74%" />
                            </Box>
                            <Skeleton variant="rounded" height={42} />
                          </Box>
                        ) : contentThreadId ? (
                          <ChatInterface
                            currentUser={user}
                            customTitle="Conversación de contenido"
                            customSubtitle="Intercambio entre cliente y equipo"
                            showThreadHeader={false}
                            customMessages={conversationMessages}
                            threadId={contentThreadId}
                            participants={contentThreadParticipants}
                            onMessagesUpdate={handleConversationMessagesUpdate}
                            onMessageSent={handleConversationRefresh}
                            composerDisabled={isDraft}
                            composerDisabledMessage="No puedes enviar mensajes mientras el contenido está en borrador. Envíalo al cliente para habilitar la conversación."
                            mentionsZIndex={9999}
                            mentionsSuggestionsPortal={true}
                          />
                        ) : (
                          <Box
                            sx={{
                              height: "100%",
                              display: "flex",
                              alignItems: "center",
                              justifyContent: "center",
                              color: neutrals.gray[600],
                              textAlign: "center",
                              px: 2,
                            }}
                          >
                            <Typography variant="body2">
                              Este contenido aún no tiene thread asociado.
                            </Typography>
                          </Box>
                        )}
                      </Box>
                    </Box>

                  </>
                )}
              </Box>
              </>
            )}
          </Box>
          {isClientView && mode !== "create" && canShowClientAcceptAction && !isMobile && activeWorkflowView === "generation" && (
            <Box
              sx={{
                display: "flex",
                justifyContent: "flex-end",
                alignItems: "center",
                gap: 1,
                px: { xs: 2, md: 3 },
                py: 1.5,
                borderTop: `1px solid ${neutrals.gray[200]}`,
                flexShrink: 0,
              }}
            >
              <Button
                onClick={() => setShowApproveDialog(true)}
                disabled={statusActionDisabled}
              >
                <Check size={16} />
                Aceptar contenido
              </Button>
            </Box>
          )}

      </Box>
      {previewImageUrl && <ImageLightbox url={previewImageUrl} onClose={() => setPreviewImageUrl("")} />}
      {/* Version History Dialog */}
      <UnifiedModal
        open={showVersionHistoryDialog}
        onClose={() => setShowVersionHistoryDialog(false)}
        title="Historial de versiones"
        maxWidth="sm"
      >
        <Box sx={{ display: "flex", flexDirection: "column", gap: 1.5, minHeight: "320px" }}>
          <Box sx={{ display: "flex", alignItems: "center", justifyContent: "space-between", gap: 1.5, flexWrap: "wrap" }}>
            <Box sx={{ display: "flex", alignItems: "center", gap: 1 }}>
              <History size={18} color={neutrals.gray[600]} />
              <Typography variant="subtitle1" sx={{ fontWeight: 600 }}>
                Historial de versiones
              </Typography>
            </Box>
            {versions.length >= 2 && !isReadOnly && (
              <Button
                variant="outline"
                size="sm"
                onClick={handleRevert}
                disabled={isReverting}
              >
                {isReverting ? (
                  <CircularProgress size={14} sx={{ mr: 1 }} />
                ) : (
                  <RotateCcw size={14} style={{ marginRight: 6 }} />
                )}
                Volver a versión anterior ({versions[1]?.version_number})
              </Button>
            )}
          </Box>

          {showVersionsSkeleton ? (
            <Box sx={{ display: "flex", flexDirection: "column", gap: 1.5 }}>
              {[1, 2, 3].map((i) => (
                <Box
                  key={i}
                  sx={{
                    p: 2,
                    borderRadius: "12px",
                    border: `1px solid ${neutrals.gray[200]}`,
                    backgroundColor: neutrals.gray[50],
                  }}
                >
                  <Box sx={{ display: "flex", alignItems: "center", justifyContent: "space-between" }}>
                    <Box sx={{ display: "flex", alignItems: "center", gap: 1 }}>
                      <Skeleton variant="rounded" width={24} height={24} sx={{ borderRadius: "6px" }} />
                      <Skeleton variant="rounded" width={90} height={20} sx={{ borderRadius: "4px" }} />
                      <Skeleton variant="text" width={120} height={16} />
                    </Box>
                    <Skeleton variant="circular" width={16} height={16} />
                  </Box>
                </Box>
              ))}
            </Box>
          ) : isLoadingVersions ? null : versions.length === 0 ? (
            <Box
              sx={{
                display: "flex",
                flexDirection: "column",
                alignItems: "center",
                justifyContent: "center",
                flex: 1,
                color: neutrals.gray[500],
                textAlign: "center",
                p: 2,
              }}
            >
              <History size={32} style={{ marginBottom: 8, opacity: 0.5 }} />
              <Typography variant="body2">
                No hay versiones registradas
              </Typography>
            </Box>
          ) : (
            <Box
              sx={{
                flex: 1,
                overflow: "auto",
                display: "flex",
                flexDirection: "column",
                gap: 1.5,
                pb: 1,
              }}
            >
              {versions.map((version, index) => {
                const isExpanded = expandedVersionId === version.id;
                const isCurrentVersion = index === 0;
                const previousVersion = versions.find(
                  (item) => item.version_number === version.version_number - 1
                );
                return (
                  <Box
                    key={version.id}
                    sx={{
                      p: 2,
                      borderRadius: "12px",
                      border: `1px solid ${neutrals.gray[200]}`,
                      backgroundColor: neutrals.gray[50],
                      transition: "all 0.2s ease",
                      "&:hover": {
                        borderColor: neutrals.gray[300],
                      },
                    }}
                  >
                    <Box
                      onClick={() => setExpandedVersionId(isExpanded ? null : version.id)}
                      sx={{
                        display: "flex",
                        alignItems: "center",
                        justifyContent: "space-between",
                        cursor: "pointer",
                      }}
                    >
                      <Box sx={{ display: "flex", alignItems: "center", gap: 1, flexWrap: "wrap" }}>
                        <Box
                          sx={{
                            width: 20,
                            height: 20,
                            borderRadius: "10px",
                            backgroundColor: colors.primary.main,
                            color: neutrals.white,
                            display: "flex",
                            alignItems: "center",
                            justifyContent: "center",
                            fontSize: "0.75rem",
                            fontWeight: 600,
                            flexShrink: 0,
                          }}
                        >
                          {version.version_number}
                        </Box>
                        <Box
                          sx={{
                            px: 1,
                            py: 0.25,
                            borderRadius: "4px",
                            backgroundColor: version.is_client
                              ? `${changeTypeColors.client_edit}20`
                              : `${changeTypeColors[version.change_type] || neutrals.gray[400]}20`,
                            color: version.is_client
                              ? changeTypeColors.client_edit
                              : changeTypeColors[version.change_type] || neutrals.gray[600],
                            fontSize: "0.7rem",
                            fontWeight: 500,
                          }}
                        >
                          {version.is_client
                            ? "Edición del cliente"
                            : (CHANGE_TYPE_LABELS[version.change_type] || version.change_type)}
                        </Box>
                        <Box sx={{ display: "flex", alignItems: "center", gap: 0.5, color: neutrals.gray[500] }}>
                          <Clock size={12} />
                          <Typography variant="caption">
                            {new Date(version.created_at).toLocaleString("es-ES", {
                              day: "2-digit",
                              month: "short",
                              hour: "2-digit",
                              minute: "2-digit",
                            })}
                          </Typography>
                          <Typography variant="caption" sx={{ ml: 0.5 }}>
                            • {version.created_by_name}
                          </Typography>
                        </Box>
                      </Box>
                      <Box sx={{ display: "flex", alignItems: "center", gap: 1 }}>
                        {isCurrentVersion && (
                          <Box
                            sx={{
                              px: 1,
                              py: 0.25,
                              borderRadius: "4px",
                              backgroundColor: `${colors.primary.main}15`,
                              color: colors.primary.main,
                              fontSize: "0.65rem",
                              fontWeight: 600,
                              border: `1px solid ${colors.primary.main}30`,
                            }}
                          >
                            Versión actual
                          </Box>
                        )}
                        {isExpanded ? (
                          <ChevronUp size={16} color={neutrals.gray[500]} />
                        ) : (
                          <ChevronDown size={16} color={neutrals.gray[500]} />
                        )}
                      </Box>
                    </Box>
                    <CollapsibleContent open={isExpanded}>
                      {previousVersion ? (
                        <Box
                          sx={{
                            mt: 1.5,
                            pt: 1.5,
                            borderTop: `1px solid ${neutrals.gray[200]}`,
                          }}
                        >
                          <Typography
                            variant="caption"
                            sx={{ color: neutrals.gray[500], fontStyle: "italic", mb: 1, display: "block" }}
                          >
                            Comparado con la version {previousVersion.version_number}
                          </Typography>
                          <Box
                            sx={{
                              fontSize: "0.85rem",
                              lineHeight: 1.7,
                              wordBreak: "break-word",
                              backgroundColor: neutrals.gray[50],
                              padding: 1.5,
                              borderRadius: "8px",
                              "& strong": { fontWeight: 600 },
                              "& em": { fontStyle: "italic" },
                              "& a": { color: theme.palette.primary.main, textDecoration: "underline" },
                            }}
                            dangerouslySetInnerHTML={{
                              __html: (() => {
                                const dmp = new DiffMatchPatch();
                                dmp.Diff_Timeout = 1;
                                const diffs = dmp.diff_main(
                                  previousVersion.text_snapshot || "",
                                  version.text_snapshot || ""
                                );
                                dmp.diff_cleanupSemantic(diffs);

                                return diffs.map(([type, text]) => {
                                  const html = markdownToHtmlSimple(text);
                                  if (type === 1) {
                                    // Añadido - verde
                                    return `<span style="background-color: ${theme.palette.success.main}25; color: ${theme.palette.success.dark}; border-radius: 2px; padding: 0 2px;">${html}</span>`;
                                  }
                                  if (type === -1) {
                                    // Eliminado - rojo tachado
                                    return `<span style="background-color: ${theme.palette.error.main}20; color: ${theme.palette.error.dark}; text-decoration: line-through; border-radius: 2px; padding: 0 2px;">${html}</span>`;
                                  }
                                  return html;
                                }).join("");
                              })()
                            }}
                          />
                        </Box>
                      ) : (
                        <Box
                          sx={{
                            color: neutrals.gray[600],
                            fontSize: "0.85rem",
                            lineHeight: 1.7,
                            mt: 1.5,
                            pt: 1.5,
                            borderTop: `1px solid ${neutrals.gray[200]}`,
                            "& strong": { fontWeight: 600 },
                            "& em": { fontStyle: "italic" },
                            "& a": { color: theme.palette.primary.main, textDecoration: "underline" },
                          }}
                          dangerouslySetInnerHTML={{
                            __html: markdownToHtmlSimple(version.text_snapshot)
                          }}
                        />
                      )}
                    </CollapsibleContent>
                  </Box>
                );
              })}
            </Box>
          )}
        </Box>
      </UnifiedModal>
      {/* Iterate Adaptation Dialog */}
      <UnifiedModal
        open={showIterateDialog}
        onClose={() => setShowIterateDialog(false)}
        title="Regenerar contenido con IA"
        maxWidth="sm"
      >
        <Box sx={{ display: "flex", flexDirection: "column", gap: 2 }}>
          <Typography variant="body2" sx={{ color: neutrals.gray[600] }}>
            Describe los cambios que quieres hacer en el contenido. Cuanto más contexto proporciones, mejor será el contenido generado por la IA.
          </Typography>
          <Typography
            variant="body2"
            sx={{ color: neutrals.gray[600], fontStyle: "italic" }}
          >
            Estos cambios se aplicarán automáticamente al contenido.
          </Typography>

          <CustomTextArea
            rows={3}
            value={iteratePrompt}
            onChange={(event) => setIteratePrompt(event.target.value)}
            placeholder="Ej: Hazlo más informal y añade emojis..."
          />
          <AgencyPromptsButton
            steps={ITERATION_PROMPT_STEPS}
            clientId={workflowClientId}
            sx={{ alignSelf: "flex-start", mt: -1 }}
          />

          <Box sx={{ display: "flex", gap: 2, justifyContent: "flex-end" }}>
            <Button variant="outline" onClick={() => setShowIterateDialog(false)}>
              Cancelar
            </Button>
            <Button
              onClick={handleIterateSubmit}
              disabled={!iteratePrompt.trim() || isIterating}
            >
              Regenerar
            </Button>
          </Box>
        </Box>
      </UnifiedModal>

      {/* Iterate Raw Content Dialog */}
      <UnifiedModal
        open={showIterateRawDialog}
        onClose={() => setShowIterateRawDialog(false)}
        title="Regenerar contenido base con IA"
        maxWidth="sm"
      >
        <Box sx={{ display: "flex", flexDirection: "column", gap: 2 }}>
          <Typography variant="body2" sx={{ color: neutrals.gray[600] }}>
            Describe los cambios que quieres hacer en el contenido base. Cuanto más contexto proporciones, mejor será el contenido generado por la IA.
          </Typography>
          <Typography
            variant="body2"
            sx={{ color: neutrals.gray[600], fontStyle: "italic" }}
          >
            Estos cambios se aplicarán automáticamente al contenido base.
          </Typography>

          <CustomTextArea
            rows={3}
            value={iterateRawPrompt}
            onChange={(event) => setIterateRawPrompt(event.target.value)}
            placeholder="Ej: Hazlo más informal y añade más detalles sobre el producto..."
          />
          <AgencyPromptsButton
            steps={ITERATION_PROMPT_STEPS}
            clientId={workflowClientId}
            sx={{ alignSelf: "flex-start", mt: -1 }}
          />

          <Box sx={{ display: "flex", gap: 2, justifyContent: "flex-end" }}>
            <Button variant="outline" onClick={() => setShowIterateRawDialog(false)}>
              Cancelar
            </Button>
            <Button
              onClick={handleIterateRawSubmit}
              disabled={!iterateRawPrompt.trim() || isIteratingRawContent}
            >
              Regenerar
            </Button>
          </Box>
        </Box>
      </UnifiedModal>

      <ConnectWarning
        open={Boolean(connectRequest)}
        platforms={connectRequest ? [connectRequest.platform] : []}
        onCancel={() => {
          connectRequest?.resolve(false);
          setConnectRequest(null);
        }}
        onConfirm={() => openConnectPortal(connectRequest)}
      />

      {/* Send to Client Confirmation Dialog */}
      <ContentConfirmationModal
        open={showSendToClientDialog}
        onClose={() => setShowSendToClientDialog(false)}
        onConfirm={handleSendToClient}
        action="send_to_client"
        contentData={{
          group_id: pieceGroupId,
          group_name: pieceGroupName,
          title: localContentDetail?.title,
        }}
        isLoading={isSaving}
      />

      {/* Approve Content Confirmation Dialog */}
      <ContentConfirmationModal
        open={showApproveDialog}
        onClose={() => setShowApproveDialog(false)}
        onConfirm={handleApproveContent}
        action="approve"
        contentData={{
          group_id: pieceGroupId,
          group_name: pieceGroupName,
          title: localContentDetail?.title,
        }}
        isLoading={isSaving}
      />

    </>
  );
}
